/** @typedef {import('headlight-configurable-application').Pict} Pict */

// The pict-section-form input-type adapter from pict-section-picker (host-agnostic). This Headlight
// subclass supplies the HOST-SPECIFIC context the module deliberately knows nothing about: project
// scoping for project-resident entities + the spec-set floor on the lab axis. It is the
// select2-free replacement for EntitySelectorMultiple — same Entity/Multiple descriptor config, but
// rendered by the pict-native picker.
const PictInputTypePicker = require('pict-section-picker/form').PictInputTypePicker;
// The values a descriptor's picker paints as "nothing selected" — handed to the widget via _resolveEmptyValues
// and used to keep `__Record` in step with what the control shows.
const libPickerEmptyValues = require('./_PickerEmptyValues.js');
// Reads by a non-simple identity share an in-flight request with any other selector reading the same row.
const libSharedEntityRead = require('./_SharedEntityRead.js');

// Headlight override CSS for the Picker — injected once (higher CSSMap priority than pict-section-picker's
// base `.pps` block so it wins, and the selectors out-specify the base rules regardless). Theme-aware via
// --bulma-*/--theme-color-* tokens (light + dark inherit for free). Three concerns:
//   1. Disabled parity (PictForm.Disabled) — a greyed, non-interactive box matching the sibling disabled
//      inputs, using the exact --bulma-input-disabled-* palette (headlight-styles/bulma-extended.scss):
//      borderless (the disabled border token equals the fill), light-gray fill, muted text/placeholder,
//      chevron and clear/chip × hidden (a visible chevron on a disabled control was a Select2 quirk). Scoped to a
//      `data-disabled` host marker the broker sets per field, so it never restyles a genuine ReadOnly
//      picker (whose interactive widget also carries `.pps-readonly`); the dropdown stays hidden even
//      under a stray keyboard-open.
//   2. EntityTag pill (Edit theme) — the pill color follows SELECTION so selected items scan at a glance:
//      UNSELECTED dropdown options keep pict-section-picker's muted base `.pps-tag`; SELECTED options
//      (`.pps-option.pps-selected`) + the in-field chips (`.pps-chip`) / single value (`.pps-valuebox`)
//      get a solid brand pill with near-white text. A color transition is set for the fade, but note the
//      dropdown re-renders its option list on select, so the smooth toggle-fade only shows where the
//      element persists (a class-toggle in PSP `_renderList` would make it fully animate — publish-gated).
//      (Read/Print `.select2-*` pill keeps its own bulma-print look, unchanged.)
//   3. Blank-display fallback — a selection with no display text and no record ID to label it by (an entity
//      record with no name reads `Document #282` instead; see _labelBlankEntityOption) shows a faded marker
//      so a user/developer can spot it: Edit paints nothing into the text spans, so an :empty::after
//      supplies it; Read/Print paints the `.picker-display-empty` span (see PickerDisplay).
//   4. Placeholder color — align the picker's empty-state placeholder to Bulma's faded --bulma-input-
//      placeholder-color so it matches native inputs (PSP's default --theme-color-text-muted read darker).
//   5. Split-pill chip (Edit) — an in-field chip whose EntityTag leads is fused into ONE pill: the chip
//      drops its left/top/bottom padding and the leading `.pps-tag` fills the left flush + full-height
//      with squared right corners, so it reads as a single pill split in half (dark entity | light value).
//      Scoped via :has(> .pps-tag:first-child) so tagless chips + the dropdown options are untouched.
//   6. Addons flush (Bulma has-addons, e.g. the Sample template's search-panel button): Bulma squares the
//      touching corners of ITS controls (.input/.button/.select) only — the picker's .pps-control keeps
//      rounded corners against the addon button. Square the touching edge on both sides.
//   7. Validation state — InputValidation marks the field's `.control.control-status` wrapper is-danger /
//      is-success; Bulma only recolors native .input/.textarea from it, so the picker's .pps-control border
//      follows the same state (the Select2 twin is in headlight-styles bulma-control-status.scss).
//   8. Solver background color — ColorInputBackground / ColorInputBackgroundTabular set an inline
//      background-color on the host (it carries the ControlAttr), which PSP's opaque .pps-control would hide;
//      the control inherits it instead, and goes back to its own fill once the inline color is cleared.
const _HEADLIGHT_PICKER_OVERRIDES_CSS = /*css*/`
.pps-form-host[data-disabled="true"] { pointer-events: none; }
.pps-form-host[data-disabled="true"] .pps-control {
	background: var(--bulma-input-disabled-background-color);
	border-color: var(--bulma-input-disabled-border-color);
	box-shadow: none;
	color: var(--bulma-input-disabled-color);
	cursor: not-allowed;
}
.pps-form-host[data-disabled="true"] .pps-control:hover { border-color: var(--bulma-input-disabled-border-color); box-shadow: none; }
.pps-form-host[data-disabled="true"] .pps-value { color: var(--bulma-input-disabled-color); }
.pps-form-host[data-disabled="true"] .pps-value.pps-placeholder,
.pps-form-host[data-disabled="true"] .pps-chips-ph { color: var(--bulma-input-disabled-placeholder-color); }
.pps-form-host[data-disabled="true"] .pps-chevron,
.pps-form-host[data-disabled="true"] .pps-clear,
.pps-form-host[data-disabled="true"] .pps-chip-x { display: none; }
.pps-form-host[data-disabled="true"] .pps-pop,
.pps-form-host[data-disabled="true"] .pps-backdrop { display: none; }
.pps-tag { transition: background-color 0.15s ease, color 0.15s ease; }
.pps-chip .pps-tag,
.pps-valuebox .pps-tag,
.pps-option.pps-selected .pps-tag {
	background: var(--theme-color-brand-primary, #156dd1);
	color: color-mix(in srgb, var(--theme-color-brand-primary, #156dd1) 12%, #ffffff);
}
.pps-chip:has(> .pps-tag:first-child) { padding-top: 0; padding-bottom: 0; padding-left: 0; }
.pps-chip:has(> .pps-tag:first-child) > .pps-tag:first-child {
	align-self: stretch;
	padding: 0.2rem 0.45rem;
	border-radius: 6px 0 0 6px;
}
.pps-value:empty::after,
.pps-chip-text:empty::after,
.pps-option-label:empty::after { content: 'no text provided'; font-style: italic; opacity: 0.55; }
.picker-display-empty { font-style: italic; opacity: 0.55; }
.pps-value.pps-placeholder,
.pps-chips-ph { color: var(--bulma-input-placeholder-color, var(--bulma-text-weak)); }
.field.has-addons .control:not(:last-child) .pps-control {
	border-top-right-radius: 0;
	border-bottom-right-radius: 0;
}
.field.has-addons .control:not(:first-child) .pps-control {
	border-top-left-radius: 0;
	border-bottom-left-radius: 0;
}
.control.control-status.is-danger .pps-control { border-color: var(--bulma-danger); }
.control.control-status.is-success .pps-control { border-color: var(--bulma-success); }
.pps-form-host[style*="background-color"] > .pps,
.pps-form-host[style*="background-color"] .pps-control { background-color: inherit; }
`;

/** @type {Record<string, any>} */
const _DEFAULT_CONFIGURATION =
{
	ProviderIdentifier: 'Picker',
	AutoInitialize: true,
	AutoInitializeOrdinal: 0,
	AutoSolveWithApp: false,
};

// R2 port-forward map for the specialized per-entity selectors (LabSelector, PlantSelector, …) —
// their Entity is provider-hardcoded, not in the descriptor. Shared with PictProvider-InputType-
// PickerDisplay so the Edit picker and the Read/Print display infer the entity identically.
const _SPECIALIZED_SELECTOR_MAP = require('./_SpecializedSelectorMap.js');
const libVocabularySelectorMap = require('./_VocabularySelectorMap.js');
// Shared with PictProvider-InputType-PickerDisplay so Edit and Read/Print both ignore a static select's Entity.
const libStaticSelectEntity = require('./_StaticSelectEntity.js');
const libEntityOptionSource = require('./_EntityOptionSource.js');
const libFilterToggleUI = require('./_FilterToggleUI.js');
const libLabRouting = require('./_LabRouting.js');
const libEntityDisplayName = require('./_EntityDisplayName.js');

// Spec-set floors — the SpecScopeResolver getter + FoxHound id-field per entity. Kept in lockstep
// with the MES's SPEC_SET_AXIS (defined in-broker to avoid coupling to the retiring MES module).
// Materials, tests and pay items narrow through their DynamicFilters entries; the lab axis is the
// one remaining set-membership floor.
const _SPEC_SET_AXIS =
{
	Lab: { getter: 'getAllowedLabIDs', field: 'IDLab' },
};

class PictProviderInputTypePicker extends PictInputTypePicker
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DEFAULT_CONFIGURATION, pOptions), pServiceHash);
		/** @type {Pict} */
		this.pict;
		// Inherited fable-service member the duplicate-pict-provider type chain hides from tsc.
		/** @type {Record<string, any>} */
		this.options;
		// Host-app onChange subscriptions (the legacy per-selector addOnChangeCallback API — e.g. the
		// bridge depth-soundings view re-solves when its BridgeSelector field changes). One broker serves
		// every InputType, so each entry may carry an InputType filter.
		/** @type {Array<{Callback: Function, InputType: (string|null)}>} */
		this.onChangeCallbacks = [];
	}

	/**
	 * Subscribe to committed selections, ported from the legacy selector providers' addOnChangeCallback
	 * (BridgeSelector et al.). The callback receives (pView, pInput, pValue, pHTMLSelector). Because the
	 * Picker broker serves every InputType, pass pInputTypeFilter (e.g. 'BridgeSelector') to hear only
	 * that InputType's fields; omit it to hear every picker field.
	 * @param {Function} pCallback @param {string} [pInputTypeFilter]
	 */
	addOnChangeCallback(pCallback, pInputTypeFilter)
	{
		if (!pCallback || typeof pCallback !== 'function')
		{
			this.pict.log.error('Invalid onChangeCallback provided.');
			return;
		}
		// Lazy init: test harnesses build the provider via Object.create (no constructor run).
		if (!Array.isArray(this.onChangeCallbacks)) { this.onChangeCallbacks = []; }
		if (this.onChangeCallbacks.some((pEntry) => pEntry.Callback === pCallback))
		{
			this.pict.log.warn('onChangeCallback already exists in the list.');
			return;
		}
		this.onChangeCallbacks.push({ Callback: pCallback, InputType: pInputTypeFilter || null });
	}

	/** Fire any subscribed onChange callbacks whose InputType filter matches this input (legacy parity:
	 *  fired ahead of the rest of the change handling, args (pView, pInput, pValue, pHTMLSelector)). */
	_fireOnChangeCallbacks(pView, pInput, pValue, pHTMLSelector)
	{
		if (!Array.isArray(this.onChangeCallbacks) || this.onChangeCallbacks.length < 1) { return; }
		const tmpInputType = pInput?.PictForm?.InputType;
		this.onChangeCallbacks.forEach((pEntry) =>
		{
			if (pEntry.InputType && pEntry.InputType !== tmpInputType) { return; }
			try { pEntry.Callback(pView, pInput, pValue, pHTMLSelector); }
			catch (pError) { this.pict.log.error(`Picker onChangeCallback threw for [${pInput?.Hash}]:`, pError); }
		});
	}

	/**
	 * Map the EntitySelectorMultiple descriptor vocabulary onto the picker's, so migrating a schema is
	 * just an InputType change: IdentityField → ValueField, EntityDisplayField → TextField (+ search it).
	 *
	 * @param {Record<string, any>} pInput @param {string} pHostSelector @param {Function} fOnChange
	 * @return {Record<string, any>}
	 */
	_buildPickerConfig(pInput, pHostSelector, fOnChange)
	{
		libStaticSelectEntity.dropStaticSelectEntity(pInput && pInput.PictForm);
		const tmpConfig = super._buildPickerConfig(pInput, pHostSelector, fOnChange);
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		// R2 port-forward: a specialized selector (LabSelector, PlantSelector, …) hardcodes its Entity and display
		// config in its legacy provider. Infer them from the InputType BEFORE the generic (non-entity) Select
		// fallback below, which would otherwise treat an entity-less descriptor as a static option list. A
		// descriptor naming the same Entity still gets the map's config (legacy ignored the descriptor's Entity);
		// display config the descriptor sets itself wins. See _SPECIALIZED_SELECTOR_MAP.
		const tmpSpecialized = _SPECIALIZED_SELECTOR_MAP[tmpPictForm.InputType];
		if (tmpSpecialized && (!tmpConfig.Entity || tmpConfig.Entity === tmpSpecialized.Entity))
		{
			tmpConfig.Entity = tmpSpecialized.Entity;
			if (!tmpConfig.TextField && !tmpPictForm.EntityDisplayField && tmpSpecialized.TextField) { tmpConfig.TextField = tmpSpecialized.TextField; }
			if (!tmpConfig.SearchFields && Array.isArray(tmpSpecialized.SearchFields)) { tmpConfig.SearchFields = tmpSpecialized.SearchFields.slice(); }
			if (!tmpConfig.EntityTag && tmpSpecialized.EntityTag) { tmpConfig.EntityTag = tmpSpecialized.EntityTag; }
		}
		// The config map's placeholder applies whenever the descriptor sets none, Entity or not (legacy parity).
		// Test the descriptor: PSP's adapter always pre-fills a default Placeholder.
		if (!tmpPictForm.Placeholder && tmpSpecialized?.Placeholder) { tmpConfig.Placeholder = tmpSpecialized.Placeholder; }
		if (!tmpConfig.ValueField && tmpPictForm.IdentityField) { tmpConfig.ValueField = tmpPictForm.IdentityField; }
		if (!tmpConfig.TextField && tmpPictForm.EntityDisplayField) { tmpConfig.TextField = tmpPictForm.EntityDisplayField; }
		if (!tmpConfig.SearchFields && tmpConfig.TextField) { tmpConfig.SearchFields = [ tmpConfig.TextField ]; }
		// Composed, disambiguated display for User pickers — mirror the recordset entity-filter "list entry"
		// (name + email/login) so a FORM's assignee / created-by picker reads the same as the list filters,
		// instead of one ambiguous field. Applies to every app's User pickers; a schema can override by
		// setting its own PictForm.TextTemplate. (The per-entity template should eventually live on Stricture.)
		if (tmpConfig.Entity === 'User' && !tmpConfig.TextTemplate)
		{
			this._registerUserListEntryTemplates();
			tmpConfig.TextTemplate = '{~DWTF:Record.NameFull:HL-EntityListEntry-User-NameParts~} ({~DWTF:Record.Email:HL-EntityListEntry-User-Login~})';
			// Make the disambiguators searchable too (so you can type an email/login, not just a name).
			if (!tmpPictForm.SearchFields)
			{
				tmpConfig.SearchFields = [ 'NameFirst', 'NameLast', 'Email', 'LoginID' ];
			}
		}

		// A–Z like legacy: every select2 entity selector sorted by its label (Select2Util `FSF~<field>~ASC~0`).
		// PictForm.Sort (else the config map's Sort) names another field or `false` opts out; a dotted label path cannot be sorted server-side.
		if (tmpConfig.Entity && tmpConfig.Sort === undefined)
		{
			let tmpSortField = tmpPictForm.Sort;
			if (tmpSortField === undefined && tmpSpecialized && (tmpConfig.Entity === tmpSpecialized.Entity))
			{
				tmpSortField = tmpSpecialized.Sort;
			}
			if (tmpSortField === undefined) { tmpSortField = tmpConfig.TextField || ((tmpConfig.Entity === 'User') ? 'NameFirst' : 'Name'); }
			if (tmpSortField && !String(tmpSortField).includes('.')) { tmpConfig.Sort = tmpSortField; }
		}

		// Generic (non-entity) Select path — map the legacy SelectOptions / SelectOptionsPickList
		// vocabulary onto the picker so a Select / Option / Select2 / Select2Multiple field migrates to
		// InputType 'Picker' with no data edits. Skipped when an Entity (server path) or a native
		// Options array is already in play.
		if (!tmpConfig.Entity && (!Array.isArray(tmpConfig.Options) || tmpConfig.Options.length === 0))
		{
			this._applyGenericSelectSource(tmpConfig, pInput);
		}
		// Hide the search box on short STATIC option lists (<= 3 items) — a Yes/No/Maybe or Left/Center/Right
		// picker doesn't need one. Only when the descriptor didn't set Searchable explicitly, and only for the
		// resolved static Options path (entity pickers + dynamic picklists always server-search, so leave those).
		// AllowCustomValues keeps it too: the search box is the only place to type a new value.
		if (!tmpConfig.Entity && !tmpConfig.DataProvider && tmpPictForm.Searchable === undefined && !tmpPictForm.AllowCustomValues && Array.isArray(tmpConfig.Options))
		{
			tmpConfig.Searchable = (tmpConfig.Options.length > 3);
		}
		// AllowCustomValues (select2 `tags`) → the picker's creatable OnCreate: a typed value matching
		// no option becomes a "Create <term>" row committing the raw string as both value and label.
		if (tmpPictForm.AllowCustomValues && !tmpConfig.OnCreate)
		{
			tmpConfig.OnCreate = (pTerm) => ({ Value: pTerm, Text: pTerm });
		}
		this._applyFacetCreate(tmpConfig, pInput);
		this._applyCustomValueMemory(tmpConfig, pInput);
		if (this._isMultiplePicker(tmpPictForm))
		{
			tmpConfig.Mode = 'multi';
		}
		// Clearable parity (found live on the B1 fixture): every legacy select2 path defaulted
		// allowClear TRUE (Select2Util `?? true`, generic Select unconditional), but neither PSP's
		// form adapter nor this broker mapped it — so a migrated single picker had NO user-facing
		// clear (writing '' became unreachable, an MVP-rule functional loss). Same default + same
		// descriptor override (PictForm.AllowClear: false). PSP applies it in single mode only
		// (multi clears chip-by-chip, as before).
		if (tmpConfig.AllowClear === undefined)
		{
			// The config map's AllowClear sits between the two (legacy BridgeSelector was never clearable).
			tmpConfig.AllowClear = tmpPictForm.AllowClear ?? tmpSpecialized?.AllowClear ?? true;
		}
		// Tabular: thread the row context into the per-row BaseFilter closure (plan §5.3). The tabular mount
		// override stashes { pView, pRowIndex } transiently around this build (see _withTabularContext), so
		// capture them here — once per row, at build time — and each row's search then narrows by its OWN
		// FromRowField / per-row Show-All. Non-tabular keeps super's row-less BaseFilter (pRowIndex null → the
		// engine's per-row paths no-op, exactly as before).
		const tmpTabCtx = this._activeTabularContext;
		if (tmpTabCtx && tmpTabCtx.pRowIndex != null)
		{
			const tmpRowIndex = tmpTabCtx.pRowIndex;
			const tmpRowView = tmpTabCtx.pView;
			tmpConfig.BaseFilter = () => this.getContextualSearchFilters(pInput, tmpRowIndex, tmpRowView);
		}
		// Legacy ProjectSelector pinned the document's own project to the top of its list (GetPinnedItem).
		if (tmpPictForm.InputType === 'ProjectSelector' && !tmpConfig.PriorityValues)
		{
			const tmpDocumentProjectID = this._getDocumentProjectID();
			if (tmpDocumentProjectID) { tmpConfig.PriorityValues = [ tmpDocumentProjectID ]; }
		}
		// Pin-to-top for lab pickers: float the project's own labs above the rest of the first browse page
		// without removing anything. PSP resolves this once per data-provider closure, which createEntityPicker
		// rebuilds on every marshal-driven re-mount — so a changed anchor (a new parent lab, a new test) is
		// picked up on the next marshal rather than sticking for the session.
		this._applyLabPriorityValues(tmpConfig, pInput, tmpTabCtx);
		this._applyEmptyListLabel(tmpConfig, pInput, tmpTabCtx);
		this._applyIDMatchLabel(tmpConfig);
		// Minimum-search-length parity: legacy select2 gated searches at `MinimumInputLengthForSearch`
		// (per-input, with a HeadlightPanels global fallback; Select2Util `?? ?? 0`). Only set it when
		// non-zero so the PSP default (0 = always query) stands for the common case.
		const tmpMinSearch = tmpPictForm.MinimumInputLengthForSearch ??
			this.pict.views?.HeadlightPanels?.MinimumInputLengthForSearch ??
			0;
		if (tmpMinSearch)
		{
			tmpConfig.MinimumSearchLength = tmpMinSearch;
		}
		// PSP's default ResolveValue reads `<Entity>/<value>`, which finds a record by its numeric id only, so a
		// value committed under another identity (GUIDSample, a Test Hash) would paint as its raw value.
		const tmpIdentity = tmpConfig.Entity ? this._getRecordIdentity(pInput) : null;
		if (tmpIdentity && (tmpIdentity !== `ID${tmpConfig.Entity}`) && (typeof tmpConfig.ResolveValue !== 'function'))
		{
			const tmpResolveView = tmpTabCtx ? tmpTabCtx.pView : null;
			const tmpResolveRow = (tmpTabCtx && tmpTabCtx.pRowIndex != null) ? tmpTabCtx.pRowIndex : null;
			tmpConfig.ResolveValue = (pValue) => this._resolveIdentityOption(tmpConfig, pInput, tmpIdentity, tmpResolveView, tmpResolveRow, pValue);
		}
		// Not a pict-section-picker option: _presentEntityOption labels a nameless record with it.
		if (tmpConfig.Entity && pInput && pInput.Name)
		{
			tmpConfig.FieldLabel = pInput.Name;
		}
		return tmpConfig;
	}

	/**
	 * Label a held value whose record identity is not the numeric `ID<Entity>`: from the `__Record` the form
	 * already holds when it matches, else read by identity — the order PickerDisplay resolves the same value in.
	 * Composed by pict-section-picker's own option builder, so the label matches the value's dropdown row.
	 *
	 * @param {Record<string, any>} pConfig - the picker config (Entity, TextField, EntityTag, join settings).
	 * @param {Record<string, any>} pInput @param {string} pIdentityField
	 * @param {Object|null} pView - the form view, when the picker was mounted by one.
	 * @param {number|string|null} pRowIndex @param {any} pValue
	 * @return {Promise<Record<string, any>|null>} the option `{ Value, Text, Record[, Tag][, Tags] }`, or null.
	 */
	_resolveIdentityOption(pConfig, pInput, pIdentityField, pView, pRowIndex, pValue)
	{
		const tmpProvider = this.pict.providers['Pict-Section-Picker'];
		if (pValue === undefined || pValue === null || pValue === '' || !tmpProvider || (typeof tmpProvider._composeOption !== 'function'))
		{
			return Promise.resolve(null);
		}
		const tmpHeld = this._findHeldRecord(pView, pInput, pIdentityField, pValue, pRowIndex);
		const tmpLookup = tmpHeld ? Promise.resolve(Object.assign({}, tmpHeld)) : this._resolveEntityRecordByIdentity(pConfig.Entity, pIdentityField, pValue);
		const tmpJoinConfig = tmpProvider._resolveJoinConfig(pConfig);
		return tmpLookup
			.then((pRecord) =>
			{
				if (!pRecord)
				{
					return null;
				}
				const tmpFK = tmpJoinConfig ? pRecord[tmpJoinConfig.FKColumn] : null;
				if (tmpFK === undefined || tmpFK === null || tmpFK === '')
				{
					return pRecord;
				}
				return new Promise((resolve) =>
				{
					this.pict.EntityProvider.getEntity(tmpJoinConfig.Entity, tmpFK, (pJoinError, pJoinRecord) =>
					{
						if (!pJoinError && pJoinRecord)
						{
							pRecord.JoinRecord = pJoinRecord;
							pRecord.JoinName = pJoinRecord[tmpJoinConfig.DisplayField];
						}
						resolve(pRecord);
					});
				});
			})
			.then((pRecord) => (pRecord ? tmpProvider._composeOption(pRecord, pIdentityField, pConfig.TextField || 'Name', tmpJoinConfig,
				pConfig.EntityTag || false, pConfig.TextTemplate || false, Array.isArray(pConfig.EntityTags) ? pConfig.EntityTags : false) : null))
			.catch((pError) =>
			{
				this.pict.log.warn(`Picker [${pConfig.Entity}]: could not label [${pValue}]: ${pError?.message || pError}`);
				return null;
			});
	}

	/**
	 * The held `__Record` (or, for a multi picker, the held record in its array) whose identity is pValue.
	 * @param {Object|null} pView @param {Record<string, any>} pInput @param {string} pIdentityField @param {any} pValue
	 * @param {number|string|null} pRowIndex
	 * @return {Record<string, any>|null}
	 */
	_findHeldRecord(pView, pInput, pIdentityField, pValue, pRowIndex)
	{
		if (!pView || (typeof pView.getMarshalDestinationObject !== 'function'))
		{
			return null;
		}
		const tmpHeld = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex));
		const tmpCandidates = Array.isArray(tmpHeld) ? tmpHeld : [ tmpHeld ];
		return tmpCandidates.find((pRecord) => pRecord && (typeof pRecord === 'object') && (pRecord[pIdentityField] == pValue)) || null;
	}

	/**
	 * Install the lab pin-to-top resolver on a picker config, when the descriptor scopes by `LabScope` and the
	 * LabScopeResolver is registered. The resolver answers with the labs belonging to the project (its district
	 * lab, its unit lab — whatever the manifest's `LabAffinity` rules name), in rule order. Empty when the form
	 * configures no affinity, which leaves the picker exactly as it was.
	 *
	 * @param {Record<string, any>} pConfig - the picker config being built (mutated).
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @param {{pView: Object, pRowIndex: number|string}|null} [pTabularContext] - the row context when tabular.
	 */
	_applyLabPriorityValues(pConfig, pInput, pTabularContext)
	{
		const tmpEngine = this.pict.providers.EntityFilterEngine;
		const tmpResolver = this.pict.providers.LabScopeResolver;
		if (!tmpEngine || !tmpResolver || pConfig.PriorityValues)
		{
			return;
		}
		const tmpFilters = (pInput && pInput.PictForm) ? pInput.PictForm.DynamicFilters : null;
		if (!Array.isArray(tmpFilters))
		{
			return;
		}
		const tmpFilter = tmpFilters.find((pEntry) => pEntry && pEntry.LabScope);
		if (!tmpFilter)
		{
			return;
		}
		const tmpRowIndex = (pTabularContext && pTabularContext.pRowIndex != null) ? pTabularContext.pRowIndex : null;
		const tmpRowView = (pTabularContext && pTabularContext.pView) ? pTabularContext.pView : null;
		pConfig.PriorityValues = () =>
		{
			return tmpResolver.getPreferredLabIDs(tmpFilter.LabScope,
				{
					IDTest: tmpEngine._readLabScopeTestID(tmpFilter, pInput, tmpRowIndex, tmpRowView),
					SelectedLabIDs: libLabRouting.collectSelectedTestLabIDs(this.pict),
				});
		};
	}

	/**
	 * Run a tabular mount with { pView, pRowIndex } stashed on the instance so the synchronous
	 * _buildPickerConfig call inside it can capture the row into the per-row BaseFilter closure (plan §5.3).
	 * Mounts are synchronous and one-row-at-a-time, so a transient instance field is race-free; restored in
	 * a finally (nesting-safe) so a single (non-tabular) mount never sees a stale row. The mounted value
	 * rides along for _applyCustomValueMemory.
	 * @param {Object} pView @param {number|string} pRowIndex @param {Function} fCallback @param {any} [pValue] @return {any}
	 */
	_withTabularContext(pView, pRowIndex, fCallback, pValue)
	{
		const tmpPrior = this._activeTabularContext || null;
		this._activeTabularContext = { pView, pRowIndex, pValue };
		try { return fCallback(); }
		finally { this._activeTabularContext = tmpPrior; }
	}

	/**
	 * Whether this picker is multi-select — the single rule both the widget Mode and the `__Record`
	 * payload shape (array vs object) resolve from, so the two can't disagree.
	 *
	 * Port-forwarded legacy multi InputTypes infer it from their NAME: `Select2Multiple` (multi keyed off
	 * the InputType name in the Select provider) and `EntitySelectorMultiple` (whose provider hardcoded
	 * Multiple: true) carry no `PictForm.Multiple` flag; their templates render via the Picker but keep
	 * the original InputType, so a field needs no manifest edit to stay multi.
	 *
	 * @param {Record<string, any>} pPictForm @return {boolean}
	 */
	_isMultiplePicker(pPictForm)
	{
		if (!pPictForm) { return false; }
		return !!pPictForm.Multiple || pPictForm.InputType === 'Select2Multiple' || pPictForm.InputType === 'EntitySelectorMultiple';
	}

	/**
	 * Mount the picker view, then route an entity picker's search results and its saved-value resolve
	 * through _presentEntityOption, so a record with no name reads `Document #282` in the dropdown, in the
	 * chip a pick leaves, and when a saved form reloads. createPicker re-assigns a fresh DataProvider and
	 * ResolveValue on every mount, so the wrap is re-applied each time rather than stacked.
	 *
	 * @param {string} pPickerHash @param {Record<string, any>} pConfig @return {any} The picker view.
	 */
	_instantiatePicker(pPickerHash, pConfig)
	{
		const tmpView = super._instantiatePicker(pPickerHash, pConfig);
		if (!tmpView?.options || !pConfig?.Entity)
		{
			return tmpView;
		}
		const fLabel = (pOption) => this._presentEntityOption(pOption, pConfig);
		const fDataProvider = tmpView.options.DataProvider;
		if (typeof fDataProvider === 'function')
		{
			tmpView.options.DataProvider = (pSearchTerm, pPage) => Promise.resolve(fDataProvider(pSearchTerm, pPage)).then((pResult) =>
			{
				if (!pResult || !Array.isArray(pResult.results))
				{
					return pResult;
				}
				return Object.assign({}, pResult, { results: pResult.results.map(fLabel) });
			});
		}
		const fResolveValue = tmpView.options.ResolveValue;
		if (typeof fResolveValue === 'function')
		{
			tmpView.options.ResolveValue = (pValue) => Promise.resolve(fResolveValue(pValue)).then(fLabel);
		}
		return tmpView;
	}

	/**
	 * An entity option as this picker shows it in the dropdown, in the chip a pick leaves and when a saved value
	 * reloads: a blank label reads `<entity label> #<record ID>` (_labelBlankEntityOption).
	 *
	 * @param {Record<string, any>} pOption - a picker option `{ Value, Text, Record[, Tag][, Tags] }`.
	 * @param {Record<string, any>} pConfig - the picker config.
	 * @return {Record<string, any>} The option, or a copy of it.
	 */
	_presentEntityOption(pOption, pConfig)
	{
		return this._labelBlankEntityOption(pOption, pConfig.Entity, pConfig.FieldLabel);
	}

	/**
	 * An entity option whose label is blank takes `<entity label> #<record ID>` as its label (`Document #282`,
	 * `Contractor #3456`), the same text PickerDisplay paints for it in Read/Print. A tag of its own is kept.
	 *
	 * @param {Record<string, any>} pOption - a picker option `{ Value, Text, Record[, Tag][, Tags] }`.
	 * @param {string} pEntity - the picker's entity, naming the record's ID column.
	 * @param {string} [pFieldLabel] - the field's label, which names an Organization's role.
	 * @return {Record<string, any>} The option, or a labelled copy of it.
	 */
	_labelBlankEntityOption(pOption, pEntity, pFieldLabel)
	{
		if (!pOption || (typeof pOption !== 'object'))
		{
			return pOption;
		}
		const tmpText = (pOption.Text === undefined || pOption.Text === null) ? '' : String(pOption.Text);
		if (tmpText.trim() !== '')
		{
			return pOption;
		}
		const tmpRecordID = pOption.Record ? pOption.Record[`ID${pEntity}`] : undefined;
		const tmpID = (tmpRecordID === undefined || tmpRecordID === null || tmpRecordID === '') ? pOption.Value : tmpRecordID;
		const tmpLabel = libEntityDisplayName.unnamedEntityLabel(pEntity, tmpID, pFieldLabel);
		if (tmpLabel === '')
		{
			return pOption;
		}
		return Object.assign({}, pOption, { Text: tmpLabel });
	}

	/**
	 * Source a picker from a managed code-list vocabulary (`_VocabularySelectorMap` — SampleStatus and
	 * friends). The referencing record persists the vocabulary's CODE, and the table is small enough to
	 * read whole, so this wires a local-filtering DataProvider over the loaded rows rather than the
	 * remote-search entity path. Both the descriptor's own `SelectOptionsEntity` and a
	 * vocabulary-mapped InputType route here; a customer with no rows (or a failed read) falls back to
	 * the static `PictForm.SelectOptions` the map installs, matching the legacy Select-family provider.
	 *
	 * @param {Record<string, any>} pConfig - the picker config being built (mutated in place).
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @return {boolean} - True when this picker is vocabulary-backed and the source was installed.
	 */
	_applyVocabularySource(pConfig, pInput)
	{
		libVocabularySelectorMap.applyVocabularySource(pInput);
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		if (!tmpPictForm.SelectOptionsEntity)
		{
			return false;
		}
		const tmpPageSize = tmpPictForm.PageSize || 100;
		const fLoad = () => libEntityOptionSource.resolveOptionsAsync(this, pInput);
		pConfig.DataProvider = (pSearchTerm, pPage) =>
		{
			const tmpPage = pPage || 1;
			return fLoad().then((pOptions) =>
			{
				const tmpTerm = String(pSearchTerm || '').toLowerCase();
				const tmpMatches = tmpTerm ?
					pOptions.filter((pOption) => String(pOption.text || '').toLowerCase().includes(tmpTerm) || String(pOption.id || '').toLowerCase().includes(tmpTerm)) :
					pOptions;
				const tmpStart = (tmpPage - 1) * tmpPageSize;
				return {
					results: tmpMatches.slice(tmpStart, tmpStart + tmpPageSize).map((pOption) => ({ Value: pOption.id, Text: pOption.text })),
					hasMore: (tmpStart + tmpPageSize) < tmpMatches.length,
				};
			});
		};
		// A code retired from the managed list still has to render on an existing record, so an
		// unresolved value labels as its own code rather than blank.
		pConfig.ResolveValue = (pValue) => fLoad().then((pOptions) =>
		{
			const tmpMatch = pOptions.find((pOption) => pOption.id === pValue);
			return tmpMatch ?
				{ Value: tmpMatch.id, Text: tmpMatch.text } :
				{ Value: pValue, Text: (pValue === null || pValue === undefined) ? '' : String(pValue) };
		});
		return true;
	}

	/**
	 * Source a non-entity picker's options from the legacy Select descriptor vocabulary, so a
	 * Select / Option / Select2 / Select2Multiple field becomes InputType 'Picker' with no data edits.
	 * Mirrors PictProvider-InputType-Select: a static `PictForm.SelectOptions` array, a precomputed
	 * `SelectOptionsPickList` (DynamicMetaLists.getList), or a Dynamic picklist (paged + filtered via a
	 * DataProvider, exactly as the select2 dynamicLocalPaginationAdapter did). The Select provider's
	 * `{ id, text }` option shape is mapped to the picker's `{ Value, Text }`.
	 *
	 * @param {Record<string, any>} pConfig - the picker config being built (mutated in place).
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @return {void}
	 */
	_applyGenericSelectSource(pConfig, pInput)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		const tmpToOption = (pItem) => ({ Value: pItem.id, Text: pItem.text });
		if (this._applyVocabularySource(pConfig, pInput))
		{
			return;
		}
		const tmpPickList = tmpPictForm.SelectOptionsPickList;
		const tmpMetaLists = this.pict.providers.DynamicMetaLists;
		if (tmpPickList && tmpMetaLists)
		{
			const fPickListDataProvider = this._createDynamicPickListDataProvider(pInput);
			if (fPickListDataProvider)
			{
				pConfig.DataProvider = fPickListDataProvider;
				return;
			}
			// Precomputed picklist: resolve the full list into static Options. The config is rebuilt on every
			// marshal-driven re-mount, so a Facet rule narrows it by the rest of the row after each solve.
			if (typeof tmpMetaLists.hasList === 'function' && tmpMetaLists.hasList(tmpPickList))
			{
				pConfig.Options = this._applyFacetRules(pInput, tmpMetaLists.getList(tmpPickList) || []).map(tmpToOption);
				return;
			}
		}
		// Static inline options.
		if (Array.isArray(tmpPictForm.SelectOptions))
		{
			pConfig.Options = tmpPictForm.SelectOptions.map(tmpToOption);
		}
	}

	/**
	 * A DataProvider over the input's Dynamic `SelectOptionsPickList`: page + filter through getList on every search
	 * (startIndex / maxResults / filterString / searchFields), matching the old select2 dynamicLocalPaginationAdapter.
	 * The list's `{ id, text }` rows map to the picker's `{ Value, Text }`.
	 *
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @return {((pSearchTerm: string, pPage: number) => Promise<Record<string, any>>)|null} null when the input names no Dynamic picklist.
	 */
	_createDynamicPickListDataProvider(pInput)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		const tmpPickList = tmpPictForm.SelectOptionsPickList;
		const tmpMetaLists = this.pict.providers.DynamicMetaLists;
		if (!tmpPickList || !tmpMetaLists || !tmpMetaLists.listDefinitions || !tmpMetaLists.listDefinitions[tmpPickList] || !tmpMetaLists.listDefinitions[tmpPickList].Dynamic)
		{
			return null;
		}
		const tmpPageSize = tmpPictForm.PageSize || 100;
		const tmpSearchFields = String(tmpPictForm.DistillingDisplay || '').split(',').map((pField) => pField.trim()).filter((pField) => pField !== '');
		return (pSearchTerm, pPage) =>
		{
			const tmpPage = pPage || 1;
			const tmpRows = tmpMetaLists.getList(tmpPickList,
				{
					startIndex: (tmpPage - 1) * tmpPageSize,
					maxResults: tmpPageSize,
					filterString: String(pSearchTerm || '').toLowerCase(),
					searchFields: tmpSearchFields,
				}) || [];
			return Promise.resolve({ results: tmpRows.map((pItem) => ({ Value: pItem.id, Text: pItem.text })), hasMore: tmpRows.length >= tmpPageSize });
		};
	}

	/**
	 * Narrow a picklist by the input's pict-section-form Facet ListFilterRules (sibling values in the same
	 * tabular row, or the same view outside a table). Inputs without a Facet rule are returned unchanged:
	 * the picker has never applied ListFilterRules, so other rule types stay dormant rather than changing
	 * existing forms.
	 *
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @param {Array<{ id: string, text: string }>} pList - the picklist entries.
	 * @return {Array<{ id: string, text: string }>}
	 */
	_applyFacetRules(pInput, pList)
	{
		const tmpDistilling = this.pict.providers.ListDistilling;
		const tmpContext = this._activeTabularContext;
		if (!tmpDistilling || (typeof tmpDistilling.getFacetRule !== 'function') || !tmpDistilling.getFacetRule(pInput) || !tmpContext || !tmpContext.pView)
		{
			return pList;
		}
		return tmpDistilling.filterList(tmpContext.pView, pInput, pList, (tmpContext.pRowIndex == null) ? undefined : tmpContext.pRowIndex);
	}

	/**
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @return {Record<string, any>|null} the input's pict-section-form Facet rule, if it has one.
	 */
	_getFacetRule(pInput)
	{
		const tmpDistilling = this.pict.providers.ListDistilling;
		if (!tmpDistilling || (typeof tmpDistilling.getFacetRule !== 'function'))
		{
			return null;
		}
		return tmpDistilling.getFacetRule(pInput);
	}

	/**
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @return {boolean} whether the input has a Facet rule and the Pict-Input-FacetFilter provider, which clears its
	 * value when a sibling change leaves it invalid (unless the rule sets ClearInvalid false).
	 */
	_isClearedByFacetFilter(pInput)
	{
		const tmpRule = this._getFacetRule(pInput);
		const tmpProviders = (pInput && pInput.PictForm && Array.isArray(pInput.PictForm.Providers)) ? pInput.PictForm.Providers : [];
		return !!tmpRule && (tmpRule.ClearInvalid !== false) && tmpProviders.includes('Pict-Input-FacetFilter');
	}

	/**
	 * On a Facet input that allows custom values and reads a global join list (JoinListAddressGlobal),
	 * "Create <term>" first adds a provisional record to that join list (see _addProvisionalFacetRecord), so
	 * FacetFilter treats the new value as consistent with its siblings when it commits. When no record can be
	 * added the cleaned, re-cased term commits as a plain custom value. A join list in form data is never
	 * written, since the form data is saved with the document.
	 *
	 * @param {Record<string, any>} pConfig - the picker config being built (mutated in place).
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @return {void}
	 */
	_applyFacetCreate(pConfig, pInput)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		const tmpContext = this._activeTabularContext;
		if (!tmpPictForm.AllowCustomValues || pConfig.Entity || pConfig.DataProvider || (typeof pConfig.OnCreate !== 'function'))
		{
			return;
		}
		const tmpRule = this._getFacetRule(pInput);
		if (!tmpRule || !tmpRule.JoinListAddressGlobal || !tmpContext || !tmpContext.pView)
		{
			return;
		}
		const tmpView = tmpContext.pView;
		const tmpRowIndex = (tmpContext.pRowIndex == null) ? undefined : tmpContext.pRowIndex;
		const fCreate = pConfig.OnCreate;
		pConfig.OnCreate = (pTerm) =>
		{
			const tmpValue = this._addProvisionalFacetRecord(tmpView, pInput, tmpRowIndex, pTerm);
			return fCreate((tmpValue === null) ? pTerm : tmpValue);
		};
	}

	/**
	 * Add a provisional record for a value typed into a Facet input: the constraining siblings' values (the
	 * preceding ones under NarrowBy Preceding, every other one otherwise; '' when empty), the rule's static
	 * Facets values (a literal Value compared with EQ), the term at the input's own JoinListValueAddress, and
	 * `Provisional: true`. A sibling value the join list does not hold is written as it stands, so FacetFilter
	 * keeps it. The term is trimmed, its whitespace collapsed, and re-cased to a spelling the join list already
	 * holds at that address, whether or not a record is added. The pick lists built from the join list are then
	 * rebuilt. Nothing is added when a record already agrees with the non-empty values, or when the rule reads no
	 * global join list.
	 *
	 * @param {Object} pView - the view the input is in.
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @param {number|string|undefined} pRowIndex - the tabular row, undefined outside a table.
	 * @param {any} pTerm - the typed term.
	 * @return {string|null} the value to commit, or null when the input reads no global join list.
	 */
	_addProvisionalFacetRecord(pView, pInput, pRowIndex, pTerm)
	{
		const tmpDistilling = this.pict.providers.ListDistilling;
		const tmpRule = this._getFacetRule(pInput);
		const fClean = (pValue) => String(pValue ?? '').trim().replace(/\s+/g, ' ');
		const tmpTerm = fClean(pTerm);
		if (!tmpRule || !tmpRule.JoinListValueAddress || (tmpTerm === ''))
		{
			return null;
		}
		const tmpJoinList = this._getWritableFacetJoinList(pView, tmpRule, false);
		if (!tmpJoinList)
		{
			return null;
		}
		const tmpManifest = this.pict.manifest;
		const fValueAt = (pRecord, pAddress) => tmpManifest.getValueAtAddress(pRecord, pAddress);
		const tmpLowerTerm = tmpTerm.toLowerCase();
		const tmpSameSpelling = tmpJoinList.find((pRecord) =>
		{
			const tmpExisting = fValueAt(pRecord, tmpRule.JoinListValueAddress);
			return !tmpDistilling.isEmptyFacetValue(tmpExisting) && (fClean(tmpExisting).toLowerCase() === tmpLowerTerm);
		});
		const tmpValue = tmpSameSpelling ? String(fValueAt(tmpSameSpelling, tmpRule.JoinListValueAddress)) : tmpTerm;
		const tmpSiblings = tmpDistilling.getFacetSiblings(pView, pInput, pRowIndex);
		const tmpOwnIndex = tmpSiblings.findIndex((pSibling) => pSibling.Input === pInput);
		// Same constraining-sibling rule as ListDistilling.getFacetMatchingRecords.
		const tmpPrecedingOnly = (tmpRule.NarrowBy === 'Preceding') && (tmpOwnIndex >= 0);
		const tmpParents = tmpSiblings.filter((pSibling, pIndex) => (pSibling.Input !== pInput) && !(tmpPrecedingOnly && (pIndex > tmpOwnIndex)));
		const tmpStaticParts = this._getStaticFacetParts(tmpRule);
		const tmpParentParts = tmpParents.map((pParent) => ({ Address: pParent.Rule.JoinListValueAddress, Value: tmpDistilling.isEmptyFacetValue(pParent.Value) ? '' : pParent.Value }));
		const tmpOwnPart = { Address: tmpRule.JoinListValueAddress, Value: tmpValue };
		const tmpConstraints = tmpStaticParts.concat(tmpParentParts.filter((pPart) => pPart.Value !== ''), [ tmpOwnPart ]);
		const tmpCovered = tmpJoinList.some((pRecord) => tmpConstraints.every((pPart) =>
		{
			const tmpExisting = fValueAt(pRecord, pPart.Address);
			return String(tmpDistilling.isEmptyFacetValue(tmpExisting) ? '' : tmpExisting) === String(pPart.Value);
		}));
		if (tmpCovered)
		{
			return tmpValue;
		}
		const tmpTarget = this._getWritableFacetJoinList(pView, tmpRule, true);
		if (!tmpTarget)
		{
			return tmpValue;
		}
		const tmpRecord = {};
		tmpStaticParts.concat(tmpParentParts, [ tmpOwnPart ]).forEach((pPart) => tmpManifest.setValueAtAddress(tmpRecord, pPart.Address, pPart.Value));
		tmpRecord.Provisional = true;
		tmpTarget.push(tmpRecord);
		this._rebuildPickListsFromSource(tmpRule.JoinListAddress);
		return tmpValue;
	}

	/**
	 * A Facet rule's static constraints: Facets entries with a literal Value (no ValueAddress) compared with EQ.
	 * @param {Record<string, any>} pRule - the input's Facet rule.
	 * @return {Array<{ Address: string, Value: any }>}
	 */
	_getStaticFacetParts(pRule)
	{
		const tmpDistilling = this.pict.providers.ListDistilling;
		const tmpFacets = Array.isArray(pRule.Facets) ? pRule.Facets : [];
		// Same EQ test as ListDistilling.compareFacetValue.
		return tmpFacets
			.filter((pFacet) => pFacet && pFacet.JoinListValueAddress && !('ValueAddress' in pFacet) && !tmpDistilling.isEmptyFacetValue(pFacet.Value) && (!pFacet.Operator || (pFacet.Operator === 'EQ')))
			.map((pFacet) => ({ Address: pFacet.JoinListValueAddress, Value: pFacet.Value }));
	}

	/**
	 * The array a global Facet rule (JoinListAddressGlobal) reads its join list from, for adding records to.
	 * One that is not set yet reads as a new empty array; with pCreate it is first created as an empty array in
	 * application state (AppData, Pict, Bundle), so application state changes only when a record is added.
	 * Null for a join list in form data, or when the rule would not see a record pushed onto the returned array
	 * (a keyed object, or an address that cannot be created).
	 *
	 * @param {Object} pView - the view the input is in.
	 * @param {Record<string, any>} pRule - the input's Facet rule.
	 * @param {boolean} pCreate - whether to create a join list that is not set yet.
	 * @return {Array<any>|null}
	 */
	_getWritableFacetJoinList(pView, pRule, pCreate)
	{
		if (!pRule.JoinListAddressGlobal)
		{
			return null;
		}
		const tmpManifest = this.pict.manifest;
		const tmpApplicationState = { Fable: this.pict, Pict: this.pict, AppData: this.pict.AppData, Bundle: this.pict.Bundle };
		const tmpCurrent = tmpManifest.getValueAtAddress(tmpApplicationState, pRule.JoinListAddress);
		if ((tmpCurrent === undefined) || (tmpCurrent === null))
		{
			if (!pCreate)
			{
				return [];
			}
			tmpManifest.setValueAtAddress(tmpApplicationState, pRule.JoinListAddress, []);
		}
		// The same address space ListDistilling.getFacetJoinList reads a global join list from.
		const tmpRead = tmpManifest.getValueAtAddress(Object.assign({ View: pView }, tmpApplicationState), pRule.JoinListAddress);
		return Array.isArray(tmpRead) ? tmpRead : null;
	}

	/**
	 * Rebuild every DynamicMetaLists pick list whose source is the given application-state address.
	 * @param {string} pSourceAddress - a ListSourceAddress read with ListSourceAddressGlobal.
	 * @return {void}
	 */
	_rebuildPickListsFromSource(pSourceAddress)
	{
		const tmpMetaLists = this.pict.providers.DynamicMetaLists;
		if (!tmpMetaLists || !tmpMetaLists.listDefinitions || (typeof tmpMetaLists.rebuildListByHash !== 'function'))
		{
			return;
		}
		for (const tmpHash of Object.keys(tmpMetaLists.listDefinitions))
		{
			const tmpDefinition = tmpMetaLists.listDefinitions[tmpHash];
			if (tmpDefinition && tmpDefinition.ListSourceAddressGlobal && (tmpDefinition.ListSourceAddress === pSourceAddress))
			{
				tmpMetaLists.rebuildListByHash(tmpHash);
			}
		}
	}

	/**
	 * Keep an AllowCustomValues field's own custom values in its dropdown, as legacy select2 did (PD-4487): a
	 * bound value missing from the source list, or one created through OnCreate, stays offered after it is
	 * cleared. Legacy injected an `<option>` that lived until the list was rebuilt or the form re-rendered, so
	 * the memory resets when the source list changes (a City list after a new State) and on re-initialize.
	 * A filled list replaced by a different one (another list, or none: a new State with no Cities yet) is such a
	 * rebuild: select2 lost the bound value with its options there, so a bound value the new list lacks is not
	 * carried over but cleared once the mount is done. An empty list filling in is a first load, not a rebuild.
	 * A Facet input the FacetFilter provider clears never takes that clear (FacetFilter owns clearing there):
	 * its list changes with every sibling choice, so each new list is treated as a first load.
	 * Static Options only; entity and dynamic-picklist pickers never allowed creation here.
	 *
	 * @param {Record<string, any>} pConfig - the picker config being built (mutated in place).
	 * @param {Record<string, any>} pInput - the form input descriptor.
	 * @return {void}
	 */
	_applyCustomValueMemory(pConfig, pInput)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		if (!tmpPictForm.AllowCustomValues || pConfig.Entity || pConfig.DataProvider || !Array.isArray(pConfig.Options))
		{
			return;
		}
		const tmpContext = this._activeTabularContext;
		const tmpKey = this._getCustomValueMemoryKey(pInput, tmpContext ? tmpContext.pRowIndex : null);
		if (!tmpKey)
		{
			return;
		}
		const tmpSourceValues = pConfig.Options.map((pOption) => String(pOption.Value));
		const tmpSignature = tmpSourceValues.join('\u0000');
		if (!this._customValueMemory)
		{
			/** @type {Record<string, { Signature: string, SourceValues: Set<string>, Values: Array<string> }>} */
			this._customValueMemory = {};
		}
		const tmpEntry = this._customValueMemory[tmpKey];
		const tmpRebuilt = !!tmpEntry && (tmpEntry.Signature !== tmpSignature) && (tmpEntry.SourceValues.size > 0) && !this._isClearedByFacetFilter(pInput);
		if (!tmpEntry || tmpEntry.Signature !== tmpSignature)
		{
			this._customValueMemory[tmpKey] = { Signature: tmpSignature, SourceValues: new Set(tmpSourceValues), Values: [] };
		}
		if (tmpContext)
		{
			const tmpCandidates = this._getCustomValueCandidates(pInput, tmpContext.pValue);
			if (tmpRebuilt)
			{
				const tmpKept = tmpCandidates.filter((pValue) => this._customValueMemory[tmpKey].SourceValues.has(pValue));
				if (tmpKept.length < tmpCandidates.length)
				{
					this._queueCustomValueClear(tmpContext.pView, pInput, tmpContext.pRowIndex, tmpKept);
				}
			}
			else
			{
				tmpCandidates.forEach((pValue) => this._rememberCustomValue(tmpKey, pValue));
			}
		}
		const fCreate = pConfig.OnCreate;
		if (typeof fCreate === 'function')
		{
			pConfig.OnCreate = (pTerm) =>
			{
				const tmpRecord = fCreate(pTerm);
				if (tmpRecord && tmpRecord.Value !== undefined && tmpRecord.Value !== null)
				{
					this._rememberCustomValue(tmpKey, String(tmpRecord.Value));
				}
				return tmpRecord;
			};
		}
		const tmpRemembered = this._customValueMemory[tmpKey].Values;
		if (tmpRemembered.length > 0)
		{
			pConfig.Options = tmpRemembered.slice().reverse().map((pValue) => ({ Value: pValue, Text: pValue })).concat(pConfig.Options);
		}
	}

	/**
	 * The custom-value memory slot for a field: its Hash, plus the row for a tabular field (legacy kept one
	 * `<select>` per row, and Facet rules can give each row its own list).
	 * @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] @return {string|null}
	 */
	_getCustomValueMemoryKey(pInput, pRowIndex)
	{
		if (!pInput || !pInput.Hash)
		{
			return null;
		}
		return (pRowIndex == null) ? pInput.Hash : `${pInput.Hash}#${pRowIndex}`;
	}

	/**
	 * The bound values worth remembering: each csv part of a multi picker, the whole value of a single one (a
	 * single value may itself contain a comma), and never a blank or an unset sentinel.
	 * @param {Record<string, any>} pInput @param {any} pValue @return {Array<string>}
	 */
	_getCustomValueCandidates(pInput, pValue)
	{
		if (this._isMultiplePicker(pInput.PictForm || {}))
		{
			return this._splitPickerValues(pValue, pInput);
		}
		if (pValue === undefined || pValue === null || typeof pValue === 'object')
		{
			return [];
		}
		const tmpValue = String(pValue);
		if (tmpValue.trim() === '' || libPickerEmptyValues.isEmptyValue(pInput, tmpValue))
		{
			return [];
		}
		return [ tmpValue ];
	}

	/**
	 * Commit what a rebuilt list still offers of a field's bound value (a multi keeps its surviving parts; a
	 * single falls back to its Default, as the DistilledInput handler does). Queued, because the rebuild runs
	 * inside the sibling's change pass, and committing there would nest a second provider pass.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex @param {Array<string>} pKept
	 */
	_queueCustomValueClear(pView, pInput, pRowIndex, pKept)
	{
		const tmpValue = this._isMultiplePicker(pInput.PictForm || {}) ? pKept.join(',') : (pInput.Default ?? '');
		queueMicrotask(() => this._setValueAndCommit(pView, pInput, tmpValue, pRowIndex));
	}

	/** Remember one custom value in a memory slot, unless the slot's source list already offers it. @param {string} pKey @param {string} pValue */
	_rememberCustomValue(pKey, pValue)
	{
		const tmpEntry = this._customValueMemory && this._customValueMemory[pKey];
		if (!tmpEntry || tmpEntry.SourceValues.has(pValue) || tmpEntry.Values.includes(pValue))
		{
			return;
		}
		tmpEntry.Values.push(pValue);
	}

	/** Drop a field's remembered custom values: a re-initialized input starts from its source list, as a re-rendered select2 did.
	 *  @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex] */
	_forgetCustomValues(pInput, pRowIndex)
	{
		const tmpKey = this._getCustomValueMemoryKey(pInput, pRowIndex);
		if (tmpKey && this._customValueMemory)
		{
			delete this._customValueMemory[tmpKey];
		}
	}

	/** Register the User list-entry display fall-back templates (name parts; login when email is empty). Idempotent. */
	_registerUserListEntryTemplates()
	{
		if (this._userListEntryTemplatesRegistered) { return; }
		this._userListEntryTemplatesRegistered = true;
		this.pict.TemplateProvider.addTemplate('HL-EntityListEntry-User-NameParts', '{~D:Record.NameFirst~} {~D:Record.NameLast~}');
		this.pict.TemplateProvider.addTemplate('HL-EntityListEntry-User-Login', '{~D:Record.LoginID~}');
	}

	/**
	 * The entity this picker targets: the descriptor's own Entity, else the entity the specialized
	 * selector's provider hardcodes (R2 port-forward — see _SPECIALIZED_SELECTOR_MAP), else the entity
	 * this provider was configured with. Every place that scopes by entity (contextual filters, sample
	 * spec-year priming) resolves through here so a migrated specialized selector scopes exactly as its
	 * descriptor-carried generic cousin would.
	 *
	 * The provider-option leg mirrors `MaterialsEntitySelector._getEntity` and is what serves an
	 * InputType the map does not name — the `MaterialsHidden*` family, which carries no descriptor
	 * `Entity` and which `MaterialsCascadePicker.onInputInitialize` branches on by name.
	 *
	 * @param {Record<string, any>} pPictForm @return {string|undefined}
	 */
	_resolveEntity(pPictForm)
	{
		libStaticSelectEntity.dropStaticSelectEntity(pPictForm);
		if (pPictForm && pPictForm.Entity)
		{
			return pPictForm.Entity;
		}
		const tmpSpecialized = _SPECIALIZED_SELECTOR_MAP[pPictForm && pPictForm.InputType];
		if (tmpSpecialized && tmpSpecialized.Entity)
		{
			return tmpSpecialized.Entity;
		}
		return (this.options && this.options.Entity) || undefined;
	}

	/**
	 * Headlight contextual scoping, ported in spirit from
	 * PictProvider-InputType-EntitySelectorMultiple.localHandleSearchFilters: extra FoxHound stanzas
	 * AND-applied to the entity search. The picker module calls this on every search via its BaseFilter
	 * hook; the module stays host-agnostic, the host knows about projects + spec years.
	 *
	 * Row context (pRowIndex, pView) enables per-row `FromRowField` / `LabScope` and per-row `__ShowAll`
	 * for tabular pickers — captured into the per-row BaseFilter closure by the tabular mount override
	 * (plan §5.3). A non-tabular picker passes neither (treated as null).
	 *
	 * @param {Record<string, any>} pInput @param {number|string} [pRowIndex] @param {Object} [pView]
	 * @return {{Filters: Array<string>, BackOffFilters: Array<string>}} The pict-section-picker 1.9.0
	 *   BaseFilter split: mandatory clauses + the preferred (auto-widen-on-empty) back-off clauses.
	 */
	getContextualSearchFilters(pInput, pRowIndex, pView)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		const tmpEntity = this._resolveEntity(tmpPictForm);
		const tmpFilters = [];
		const tmpBackoff = [];
		const tmpRowIndex = (pRowIndex === undefined) ? null : pRowIndex;
		const tmpView = pView || null;
		const tmpEngine = this.pict.providers.EntityFilterEngine;
		// The engine reads StaticFilters / ShowAllToggle off the descriptor, so hand it the config-map defaults too.
		const tmpFilterInput = this._withMapFilterDefaults(pInput);
		// Show-All override, read once (per-row when tabular). Drops the back-off list + the non-mandatory
		// scope clauses (the spec-set floor unless SpecYearMandatory, and the engine's SamplePlan/Through/Lab scopes).
		const tmpOverridden = !!(tmpEngine && tmpEngine.isFilterOverridden(tmpFilterInput, tmpRowIndex));

		// Project scoping for project-resident entities. Off-switches: PictForm.disableProjectFilteringForEntity,
		// or the config map's DisableProjectFiltering (a specialized selector whose legacy provider never scoped).
		const tmpProjectEntityList = [ 'LineItem', 'BidItem' ];
		const tmpScopingDisabled = tmpPictForm.disableProjectFilteringForEntity || _SPECIALIZED_SELECTOR_MAP[tmpPictForm.InputType]?.DisableProjectFiltering;
		if (!tmpScopingDisabled && tmpProjectEntityList.includes(tmpEntity))
		{
			const tmpProjectID = this.pict.AppData?.DocumentData?.ReportData?.IDProject;
			if (tmpProjectID) { tmpFilters.push(`FBV~IDProject~EQ~${tmpProjectID}`); }
		}

		// Customer state scoping (legacy BridgeSelector): only the customer's configured state(s).
		if (_SPECIALIZED_SELECTOR_MAP[tmpPictForm.InputType]?.CustomerStateFilter)
		{
			const tmpStateClause = this._getCustomerStateClause();
			if (tmpStateClause) { tmpFilters.push(tmpStateClause); }
		}

		// Spec-set floor — ported faithfully from MES.localHandleSearchFilters. Gated by Show-All UNLESS
		// PictForm.SpecYearMandatory; routed to the back-off list when PictForm.SpecYearBackOff. The
		// resolver getter returns null on the fail-open paths (no contract FK / empty set / no join
		// rows) — leave unfiltered then.
		if (!tmpPictForm.DisableSpecYearSiloing && this.pict.providers.SpecScopeResolver)
		{
			const tmpAxis = _SPEC_SET_AXIS[tmpEntity];
			// Lab is the only axis here, and its floor must be read family-expanded: spec-set membership is
			// declared per FAMILY, so raw IDLab membership empties one level or the other (see
			// _LabRouting.resolveSpecSetLabFloor).
			const tmpAllowed = tmpAxis ? libLabRouting.resolveSpecSetLabFloor(this.pict) : null;
			if (tmpAllowed && tmpAllowed.size > 0 && (tmpPictForm.SpecYearMandatory || !tmpOverridden))
			{
				(tmpPictForm.SpecYearBackOff ? tmpBackoff : tmpFilters).push(`FBL~${tmpAxis.field}~INN~${Array.from(tmpAllowed).join(',')}`);
			}
		}

		// Descriptor-level Static/Dynamic scope filters via the extracted engine (StaticFilters, DynamicFilters
		// FromField / per-row FromRowField / SamplePlan / Through / Lab). The engine appends mandatory clauses
		// to tmpFilters and `BackOff: true` clauses to tmpBackoff. Row context threads per-row FromRowField /
		// LabScope (plan §5.3).
		if (tmpEngine)
		{
			tmpEngine.applyStaticFilters(tmpFilters, tmpFilterInput, tmpBackoff);
			tmpEngine.applyDynamicFilters(tmpFilters, tmpFilterInput, tmpRowIndex, tmpView, tmpBackoff);
		}

		// Fold in any descriptor-level scope hook (e.g. the recordset entity filter base's GetContextScopeFilter).
		const tmpBase = super.getContextualSearchFilters(pInput);
		if (tmpBase) { tmpFilters.push(Array.isArray(tmpBase) ? tmpBase.filter(Boolean).join('~') : tmpBase); }

		// The `{ Filters, BackOffFilters }` split is pict-section-picker 1.9.0's BaseFilter contract: both
		// sets apply together, and an EMPTY first page auto-retries once WITHOUT the back-off set — the
		// legacy getOptionsAjax auto-widen (§5.6), restored. Show-All drops the back-off set entirely
		// (legacy parity); the mandatory set survives.
		return {
			Filters: tmpFilters.filter(Boolean),
			BackOffFilters: tmpOverridden ? [] : tmpBackoff.filter(Boolean),
		};
	}

	/**
	 * The descriptor with its config-map filter defaults (StaticFilters, ShowAllToggle) filled in where it sets
	 * none: MaterialSelector's top-level-only filter and its toggle. A copy, and Picker-path only; the legacy
	 * Select2Util shares the filter engine and must not see the defaults before its type flips.
	 * @param {Record<string, any>} pInput @return {Record<string, any>}
	 */
	_withMapFilterDefaults(pInput)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		const tmpSpecialized = _SPECIALIZED_SELECTOR_MAP[tmpPictForm.InputType];
		if (!tmpSpecialized) { return pInput; }
		const tmpDefaults = {};
		if (tmpPictForm.StaticFilters === undefined && tmpSpecialized.StaticFilters) { tmpDefaults.StaticFilters = tmpSpecialized.StaticFilters; }
		if (tmpPictForm.ShowAllToggle === undefined && tmpSpecialized.ShowAllToggle !== undefined) { tmpDefaults.ShowAllToggle = tmpSpecialized.ShowAllToggle; }
		if (Object.keys(tmpDefaults).length < 1) { return pInput; }
		return Object.assign({}, pInput, { PictForm: Object.assign({}, tmpPictForm, tmpDefaults) });
	}

	/**
	 * Word the marker on a row found by its ID (pict-section-picker 1.9.9 `IDMatchLabel`) the way a record with
	 * no name is labelled, so a Producer field reads `Producer #3456`, not `Organization #3456`. Entity and
	 * FieldLabel are read from the config when the marker is drawn.
	 *
	 * @param {Record<string, any>} pConfig - The picker config being built; left alone if it sets its own IDMatchLabel.
	 */
	_applyIDMatchLabel(pConfig)
	{
		if (pConfig.IDMatchLabel !== undefined)
		{
			return;
		}
		pConfig.IDMatchLabel = (pRecord, pID) => libEntityDisplayName.unnamedEntityLabel(pConfig.Entity, pID, pConfig.FieldLabel);
	}

	/**
	 * Give the picker an EmptyLabel function (pict-section-picker 1.9.8) that explains an empty list its own
	 * scope narrowed. It reads the config's BaseFilter when the list renders empty, so a BaseFilter a subclass
	 * sets after this call, or a scope that changed since the last search, is the one checked.
	 *
	 * @param {Record<string, any>} pConfig - The picker config being built; left alone if it sets its own EmptyLabel.
	 * @param {Record<string, any>} pInput - The descriptor.
	 * @param {{pRowIndex?: number, pView?: Object}|null} pTabularContext - The row being mounted, for a tabular picker.
	 */
	_applyEmptyListLabel(pConfig, pInput, pTabularContext)
	{
		if (pConfig.EmptyLabel !== undefined)
		{
			return;
		}
		const tmpRowIndex = (pTabularContext && pTabularContext.pRowIndex != null) ? pTabularContext.pRowIndex : null;
		pConfig.EmptyLabel = () => this.getEmptyListLabel(pInput, pConfig, tmpRowIndex);
	}

	/**
	 * The empty-list wording the Select2 dropdowns used (PictProvider-Select2Util noResults), for an entity picker
	 * whose BaseFilter currently narrows the search: the field's FilteredNoResultsMessage, else a "Show all" hint
	 * while the field offers an override that is off, else "No matches with the current filter.". Blank (the
	 * module's own "No matches") when nothing narrowed it, and for pick-list, custom-provider and local pickers.
	 *
	 * @param {Record<string, any>} pInput - The descriptor.
	 * @param {Record<string, any>} pConfig - The picker config (Entity, BaseFilter, DataProvider, PickListDataProvider).
	 * @param {number|null} [pRowIndex] - The row of a tabular picker, for its own Show all state.
	 * @return {string}
	 */
	getEmptyListLabel(pInput, pConfig, pRowIndex)
	{
		if (!pConfig || !pConfig.Entity || pConfig.DataProvider || pConfig.PickListDataProvider)
		{
			return '';
		}
		let tmpScope = pConfig.BaseFilter;
		if (typeof tmpScope === 'function')
		{
			try
			{
				tmpScope = tmpScope();
			}
			catch (pError)
			{
				return '';
			}
		}
		if (!this._scopeNarrows(tmpScope))
		{
			return '';
		}
		const tmpFilterInput = this._withMapFilterDefaults(pInput);
		const tmpPictForm = (tmpFilterInput && tmpFilterInput.PictForm) || {};
		if (tmpPictForm.FilteredNoResultsMessage)
		{
			return tmpPictForm.FilteredNoResultsMessage;
		}
		const tmpEngine = this.pict.providers.EntityFilterEngine;
		const tmpOffersShowAll = !!(tmpPictForm.ShowAllToggle || tmpPictForm.OverrideField);
		const tmpRowIndex = (pRowIndex === undefined) ? null : pRowIndex;
		if (tmpOffersShowAll && !(tmpEngine && tmpEngine.isFilterOverridden(tmpFilterInput, tmpRowIndex)))
		{
			return 'No matches with the current filter — turn on "Show all" to see everything.';
		}
		return 'No matches with the current filter.';
	}

	/**
	 * Whether a resolved BaseFilter value (a stanza string, an array of them, or the `{ Filters, BackOffFilters }`
	 * split) holds at least one clause.
	 * @param {any} pScope @return {boolean}
	 */
	_scopeNarrows(pScope)
	{
		const fHasClause = (pPart) => Array.isArray(pPart) ? pPart.some(Boolean) : !!pPart;
		if (pScope && typeof pScope === 'object' && !Array.isArray(pScope))
		{
			return fHasClause(pScope.Filters) || fHasClause(pScope.BackOffFilters);
		}
		return fHasClause(pScope);
	}

	/**
	 * The customer's state clause for a state-scoped selector (legacy BridgeSelector), from ConfigurationProvider's
	 * `StateAbbreviation`: one state (`FBV~State~EQ`), several (`FBL~State~INN`), or none (unscoped). Cached per
	 * customer, because the lookup logs on every call and this runs on every search.
	 * @return {string|null}
	 */
	_getCustomerStateClause()
	{
		const tmpCustomerID = this.pict.AppData?.CurrentUser?.CustomerID;
		const tmpProvider = this.pict.providers.ConfigurationProvider;
		if (!tmpCustomerID || !tmpProvider || typeof tmpProvider.getConfigurationByScope !== 'function') { return null; }
		this._customerStateClauses = this._customerStateClauses || {};
		if (!(tmpCustomerID in this._customerStateClauses))
		{
			const tmpConfig = tmpProvider.getConfigurationByScope(tmpCustomerID);
			const tmpState = (tmpConfig && !(tmpConfig instanceof Error)) ? tmpConfig.StateAbbreviation : null;
			let tmpClause = null;
			if (Array.isArray(tmpState) && tmpState.length > 0) { tmpClause = `FBL~State~INN~${tmpState.map((pState) => encodeURIComponent(pState)).join(',')}`; }
			else if (tmpState && !Array.isArray(tmpState)) { tmpClause = `FBV~State~EQ~${encodeURIComponent(tmpState)}`; }
			this._customerStateClauses[tmpCustomerID] = tmpClause;
		}
		return this._customerStateClauses[tmpCustomerID];
	}

	// --- Show-All filter override (beast MVP, §8.7) — the user's escape hatch from an over-narrow filter ---

	/** The integrated "Show All" toggle element id for this picker. Broker-namespaced so it never collides
	 *  with a legacy MES toggle during coexistence. @param {string} pInputHTMLID @param {number|string} [pRowIndex] */
	getSelectFilterToggleID(pInputHTMLID, pRowIndex)
	{
		return `#PICKER-FILTER-TOGGLE-${pInputHTMLID}${(pRowIndex != null) ? `-${pRowIndex}` : ''}`;
	}

	/**
	 * Flip the "Show All" override for this picker. State persists at `<key>__ShowAll` (non-tabular) or
	 * `<key>__ShowAll_<rowIndex>` (tabular, per-row) — the same address `EntityFilterEngine.isFilterOverridden`
	 * reads in getContextualSearchFilters — so the NEXT search drops (or re-applies) the back-off + non-mandatory
	 * scope clauses. Ported from MES.toggleFilterOverride. No forced re-search is needed: PSP drops its loaded
	 * pages when the dropdown closes, and its backdrop swallows the click that closes it, so the toggle is only
	 * ever actuated while closed — the next open re-queries and re-resolves BaseFilter against the state written
	 * here. (Through PSP 1.9.0 this called a public reload() to punch through a cache that outlived the close.)
	 * @param {string} pInputHTMLID @param {string} pKey - DataAddress (non-tabular) or input Hash (tabular per-row)
	 * @param {number|string} [pRowIndex]
	 */
	toggleFilterOverride(pInputHTMLID, pKey, pRowIndex)
	{
		// A read-only / disabled picker paints the toggle inert (_FilterToggleUI) — swallow the click rather
		// than writing override state and re-composing filters for a field whose value cannot change.
		if (libFilterToggleUI.isToggleDisabled(this.pict, this.getSelectFilterToggleID(pInputHTMLID, pRowIndex))) { return; }
		const tmpSuffix = (pRowIndex != null) ? `__ShowAll_${pRowIndex}` : '__ShowAll';
		const tmpAddress = `AppData.DocumentData.ReportData.FormData.${pKey}${tmpSuffix}`;
		const tmpNewState = !this.pict.manifest.getValueByHash(this.pict, tmpAddress);
		this.pict.manifest.setValueByHash(this.pict, tmpAddress, tmpNewState);
		this._updateFilterToggleIcon(pInputHTMLID, tmpNewState, pRowIndex);
	}

	/** Repaint the toggle icon to reflect the current override state. Ported from MES._updateFilterToggleIcon.
	 *  @param {string} pInputHTMLID @param {boolean} pState @param {number|string} [pRowIndex] */
	_updateFilterToggleIcon(pInputHTMLID, pState, pRowIndex)
	{
		if (!this.pict?.ContentAssignment) { return; }
		const tmpIcon = pState ? 'fa-toggle-on' : 'fa-toggle-off';
		this.pict.ContentAssignment.assignContent(`${this.getSelectFilterToggleID(pInputHTMLID, pRowIndex)} > .icon`, `<i class="fas ${tmpIcon}"></i>`);
	}

	/**
	 * Three concerns on a committed selection:
	 *   1. Spec-year scope from the picked sample, ported from EntitySelectorMultiple.onDataChange: when a
	 *      Sample is selected, prime the SpecScopeResolver so the pickers it gates narrow.
	 *      Single-project resolver, so for a multi-select we take the first identifier (and warn).
	 *   2. Populate the field's `__Record` slot (see _refreshEntityRecord).
	 *   3. Re-sync the Required state and live validation (see _syncRequiredStateAfterCommit).
	 */
	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._fireOnChangeCallbacks(pView, pInput, pValue, pHTMLSelector);
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		if (this._resolveEntity(tmpPictForm) === 'Sample' && pValue && this.pict.providers.SpecScopeResolver)
		{
			const tmpIdentifiers = (typeof pValue === 'string' ? pValue.split(',') : (Array.isArray(pValue) ? pValue : []))
				.map((pVal) => String(pVal).trim()).filter((pVal) => pVal.length > 0);
			if (tmpIdentifiers.length > 1)
			{
				this.pict.log.warn(`Picker [${pInput?.Hash}]: multi-sample selection with spec-year scope active — using the first sample (${tmpIdentifiers[0]}) as the scope source. Picker has ${tmpIdentifiers.length} samples selected.`);
			}
			if (tmpIdentifiers.length > 0)
			{
				const tmpIdentityField = tmpPictForm.IdentityField || 'IDSample';
				this.pict.providers.SpecScopeResolver.loadForSampleByIdentifier?.(tmpIdentifiers[0], tmpIdentityField);
			}
		}
		this._publishSelectedProject(pInput, pValue);
		this._refreshEntityRecord(pView, pInput, pValue, pTransactionGUID);
		// The picker id helpers live on the (untyped-to-tsc) pict-section-picker base.
		const tmpSelf = /** @type {any} */ (this);
		this._syncRequiredStateAfterCommit(pInput, pInput.Macro.HTMLSelector, tmpSelf.getPickerHostID(pInput.Macro.RawHTMLID));
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/** Tabular twin of onDataChange's record refresh + Required re-sync — the record lands at the row's own address. */
	onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._fireOnChangeCallbacks(pView, pInput, pValue, pHTMLSelector);
		this._refreshEntityRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex);
		const tmpSelf = /** @type {any} */ (this);
		this._syncRequiredStateAfterCommit(pInput, tmpSelf.getTabularHiddenID(pInput.Macro.RawHTMLID, pRowIndex), tmpSelf.getTabularPickerHostID(pInput.Macro.RawHTMLID, pRowIndex));
		return super.onDataChangeTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/** Load-path twin of onDataChange's SelectedProject publish: legacy ProjectSelector set it on every marshal. */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._publishSelectedProject(pInput, pValue);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Legacy ProjectSelector parity: publish the chosen Project as `AppData.SelectedProject`, which LADOTD
	 * sample names read (SampleSequencing.buildSampleName → ExternalProjectID). Like legacy, an empty value
	 * leaves it as-is and tabular never publishes. The latest request wins if lookups settle out of order.
	 * @param {Record<string, any>} pInput @param {any} pValue
	 * @return {Promise<void>|void}
	 */
	_publishSelectedProject(pInput, pValue)
	{
		if (!pInput || !pInput.PictForm || pInput.PictForm.InputType !== 'ProjectSelector') { return; }
		const tmpProjectID = this._splitPickerValues(pValue, pInput)[0];
		if (!tmpProjectID || !this.pict.EntityProvider) { return; }
		const tmpRequest = this._selectedProjectRequest = {};
		return this._resolveEntityRecord('Project', tmpProjectID, pInput).then((pProject) =>
		{
			if (pProject && this._selectedProjectRequest === tmpRequest) { this.pict.AppData.SelectedProject = pProject; }
		});
	}

	// --- Entity __Record population ---

	/**
	 * The `__Record` address for this input/row, ported from MaterialsEntitySelector so a port-forwarded
	 * field's record lands at the SAME address its consumers already read. pSuffix supports the sibling
	 * slots the cascade family keeps next to the record (`__Salt`, `_DocumentPolyJoin` — legacy parity:
	 * an explicit PictForm.RecordAddress wins regardless of suffix).
	 * @param {Record<string, any>} pInput @param {number|string} [pRowIndex] @param {string} [pSuffix] @return {string}
	 */
	_getRecordAddress(pInput, pRowIndex, pSuffix = '__Record')
	{
		if (pInput.PictForm?.RecordAddress) { return pInput.PictForm.RecordAddress; }
		if (pRowIndex != null)
		{
			return this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress) + pSuffix;
		}
		return `${pInput.DataAddress || pInput.Hash}${pSuffix}`;
	}

	/**
	 * The committed picker value as a list of entity ids: multi commits csv (PSP's _commit joins the
	 * array), single commits the bare id, a cleared picker commits ''.
	 *
	 * An unset SENTINEL drops out alongside the blanks (pInput given): a pristine `DataType: Number`
	 * field marshals in as manyfest's DataType default 0, which the widget paints as its placeholder —
	 * so `__Record` must read as cleared too, not fetch the entity #0 that does not exist.
	 * @param {any} pValue @param {Record<string, any>} [pInput] @return {Array<string>}
	 */
	_splitPickerValues(pValue, pInput)
	{
		const tmpValues = Array.isArray(pValue) ? pValue : String(pValue ?? '').split(',');
		return tmpValues.map((pEntry) => String(pEntry).trim()).filter((pEntry) =>
			(pEntry.length > 0) && !(pInput && libPickerEmptyValues.isEmptyValue(pInput, pEntry)));
	}

	/** The widget's `EmptyValues` from the shared Headlight rule, so the control, `__Record` and Read/Print agree. */
	_resolveEmptyValues(pInput)
	{
		return libPickerEmptyValues.resolveEmptyValues(pInput);
	}

	/**
	 * The committed VALUE address for this input/row (not `__Record`): the slot the marshal binds the picker to.
	 * @param {Record<string, any>} pInput @param {number|string} [pRowIndex] @return {string}
	 */
	_getPickerValueAddress(pInput, pRowIndex)
	{
		if (pRowIndex != null)
		{
			return this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pInput.PictForm.InformaryDataAddress);
		}
		return pInput.PictForm?.InformaryDataAddress || pInput.DataAddress || pInput.Hash;
	}

	/**
	 * Whether the committed value moved on while a record lookup was in flight. A value nobody has written
	 * yet is no evidence either way, so it never counts as stale.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null|undefined} pRowIndex
	 * @param {Array<string>} pRequestedValues - The split values the lookup was issued for.
	 * @return {boolean}
	 */
	_isStaleEntityRecordRefresh(pView, pInput, pRowIndex, pRequestedValues)
	{
		const tmpCurrentValue = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getPickerValueAddress(pInput, pRowIndex));
		if (tmpCurrentValue === undefined || tmpCurrentValue === null)
		{
			return false;
		}
		const tmpCurrent = new Set(this._splitPickerValues(tmpCurrentValue, pInput));
		const tmpRequested = new Set(pRequestedValues);
		if (tmpCurrent.size !== tmpRequested.size)
		{
			return true;
		}
		for (const tmpValue of tmpRequested)
		{
			if (!tmpCurrent.has(tmpValue))
			{
				return true;
			}
		}
		return false;
	}

	/**
	 * One entity record for a picker value, resolved through the SAME path PSP resolves the display label
	 * with (EntityProvider.getEntity, which reads its recordCache first). The picker's own dropdown load
	 * caches every option's full record, so a selection is normally a cache hit costing no round-trip; a
	 * genuine miss (a value outside the loaded set, or one past the cache's 30s TTL) fetches. Resolves
	 * null on failure — the caller keeps whatever record is already there (offline-tolerant, as the MES's
	 * backfill is).
	 *
	 * Identity-aware (beast B2): a creation-family field commits its GUID identity (e.g. `GUIDSample`),
	 * which EntityProvider.getEntity cannot look up (it fetches by numeric id) — those route through the
	 * legacy MES `_refreshRecord` fetch shape instead (`FBL~<identity>~INN~<value>`).
	 * @param {string} pEntity @param {string} pValue @param {Record<string, any>} [pInput]
	 * @return {Promise<Record<string, any>|null>}
	 */
	_resolveEntityRecord(pEntity, pValue, pInput)
	{
		const tmpIdentity = pInput ? this._getRecordIdentity(pInput) : `ID${pEntity}`;
		if (tmpIdentity !== `ID${pEntity}`)
		{
			return this._resolveEntityRecordByIdentity(pEntity, tmpIdentity, pValue);
		}
		return new Promise((resolve) =>
		{
			this.pict.EntityProvider.getEntity(pEntity, pValue, (pError, pRecord) =>
			{
				if (pError || !libSharedEntityRead.isEntityRecord(pEntity, pRecord))
				{
					this.pict.log.warn(`Picker [${pEntity}]: record lookup failed for [${pValue}]: ${pError?.message || pError || pRecord?.Error || 'no record returned'}`);
					return resolve(null);
				}
				return resolve(pRecord);
			});
		});
	}

	/**
	 * Fetch a record by a NON-simple identity field (the legacy MES `_refreshRecord` System-2 shape), through the
	 * shared read MES uses, so concurrent reads of one row share a request and an empty or placeholder identity
	 * is never read. Resolves null on failure/no-match — same keep-prior-record semantics as the getEntity path.
	 * @param {string} pEntity @param {string} pIdentityField @param {string} pValue
	 * @return {Promise<Record<string, any>|null>}
	 */
	_resolveEntityRecordByIdentity(pEntity, pIdentityField, pValue)
	{
		return new Promise((resolve) =>
		{
			if (libSharedEntityRead.isEmptyIdentityValue(pIdentityField, pValue))
			{
				return resolve(null);
			}
			if (!this.pict.HeadlightRestClient)
			{
				this.pict.log.warn(`Picker [${pEntity}]: no HeadlightRestClient — cannot resolve by [${pIdentityField}].`);
				return resolve(null);
			}
			libSharedEntityRead.readJSON(this.pict, `${pEntity}s/FilteredTo/FBL~${pIdentityField}~INN~${encodeURIComponent(pValue)}`, { ReuseResult: false }, (pError, pRecords) =>
			{
				if (pError || !Array.isArray(pRecords) || pRecords.length < 1)
				{
					this.pict.log.warn(`Picker [${pEntity}]: record lookup by ${pIdentityField} failed for [${pValue}]: ${pError?.message || pError || 'no records found'}`);
					return resolve(null);
				}
				if (pRecords.length > 1)
				{
					this.pict.log.warn(`Picker [${pEntity}]: multiple records with ${pIdentityField} = [${pValue}]; using the first.`);
				}
				return resolve(pRecords[0]);
			});
		});
	}

	/**
	 * Populate `<DataAddress>__Record` with the full entity record(s) behind the committed value — the
	 * write the legacy MaterialsEntitySelector did on selection and the port-forwarded picker dropped
	 * (PSP fetches a record to render the label but keeps only {Value, Text, Tag}). Without it every
	 * autofill-receiver PostSolver, compound display and PDF field reads an empty record.
	 *
	 * Timing: the lookup is async, so the write is fenced with a transaction async-op — registered
	 * SYNCHRONOUSLY here, before the form view's finalizeTransaction runs in its `finally`. That holds the
	 * transaction, and therefore the AutofillTriggerGroup PostSolvers that read `__Record`, open until the
	 * record has landed: no race, no one-save lag. Same fence Pict-Provider-Input-EntityBundleRequest uses
	 * for its fetches. Registered even on the cache-hit path — the promise still settles a microtask late,
	 * and a uniform fence beats a write whose visibility depends on cache luck.
	 *
	 * See docs/Picker-EntityRecord-Timing.md.
	 *
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue
	 * @param {string} [pTransactionGUID] @param {number|string} [pRowIndex]
	 * @return {Promise<any>|void} resolves once the record has landed (the tests await it).
	 */
	_refreshEntityRecord(pView, pInput, pValue, pTransactionGUID, pRowIndex)
	{
		const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
		// A non-entity picker (a static / picklist Select port-forward) has no record to hold.
		if (!tmpEntity || !pView || typeof pView.getMarshalDestinationObject !== 'function') { return; }
		const tmpMultiple = this._isMultiplePicker(pInput.PictForm);
		const tmpRecordAddress = this._getRecordAddress(pInput, pRowIndex);
		const fWriteRecord = (pRecord) => this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress, pRecord);
		const tmpValues = this._splitPickerValues(pValue, pInput);

		// Cleared: reset the slot synchronously (no fetch) so nothing downstream reads the previous record.
		if (tmpValues.length < 1)
		{
			const tmpCleared = tmpMultiple ? [] : { };
			fWriteRecord(tmpCleared);
			// Fire the cascade seam on clear too (a subclass may clear dependent joins / receivers). Sync path:
			// the writes land in this same dataChanged cycle, before finalizeTransaction — no fence needed.
			this._onEntityRecordCascade(pView, pInput, tmpCleared, pRowIndex, pTransactionGUID);
			return;
		}
		if (!this.pict.EntityProvider)
		{
			this.pict.log.warn(`Picker [${pInput.Hash}]: no EntityProvider registered — cannot populate [${tmpRecordAddress}].`);
			return;
		}

		// Unique per (field, row, change) so concurrent pickers in one transaction can't cross-complete.
		const tmpOperationHash = `PickerRecordRefresh-${tmpEntity}-${tmpRecordAddress}-${this.pict.getUUID()}`;
		const tmpFenced = !!pTransactionGUID && typeof pView.registerEventTransactionAsyncOperation === 'function';
		if (tmpFenced) { pView.registerEventTransactionAsyncOperation(pTransactionGUID, tmpOperationHash); }
		return Promise.all(tmpValues.map((pEntityValue) => this._resolveEntityRecord(tmpEntity, pEntityValue, pInput)))
			.then((pRecords) =>
			{
				// The value moved on while the lookups were in flight; the refresh for the current value owns the record.
				if (this._isStaleEntityRecordRefresh(pView, pInput, pRowIndex, tmpValues))
				{
					return undefined;
				}
				const tmpResolved = pRecords.filter((pRecord) => !!pRecord);
				let tmpWritten;
				if (tmpMultiple)
				{
					// A failed lookup keeps the record the form holds for that value, as a failed single lookup does.
					const tmpIdentity = this._getRecordIdentity(pInput);
					tmpWritten = tmpValues.map((pEntityValue, pIndex) => pRecords[pIndex] || this._findHeldRecord(pView, pInput, tmpIdentity, pEntityValue, pRowIndex)).filter((pRecord) => !!pRecord);
					fWriteRecord(tmpWritten);
				}
				else if (tmpResolved.length > 0)
				{
					const tmpHeld = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
					tmpWritten = this._reconcileRefreshedRecord(pView, pInput, tmpHeld, tmpResolved[0], this._getRecordIdentity(pInput), tmpValues[0], pRowIndex);
					fWriteRecord(tmpWritten);
				}
				else
				{
					// Every lookup failed — keep any prior record, else normalize the empty slot to {}.
					tmpWritten = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRecordAddress);
					if (!tmpWritten) { tmpWritten = { }; fWriteRecord(tmpWritten); }
				}
				// Fire the cascade seam AFTER __Record lands and INSIDE the fence; return its result so the
				// transaction awaits an async cascade before its PostSolvers run (see _onEntityRecordCascade).
				return this._onEntityRecordCascade(pView, pInput, tmpWritten, pRowIndex, pTransactionGUID);
			})
			.catch((pError) =>
			{
				this.pict.log.error(`Picker [${pInput.Hash}]: error populating [${tmpRecordAddress}]: ${pError.message || pError}`);
			})
			.finally(() =>
			{
				if (tmpFenced) { pView.eventTransactionAsyncOperationComplete(pTransactionGUID, tmpOperationHash); }
			});
	}

	/**
	 * The per-InputType CASCADE seam for the MaterialsEntitySelector "beast" migration (plan §8.8).
	 *
	 * Fired right AFTER `__Record` is written and — on the select path — INSIDE the fenced transaction,
	 * so a subclass's cascade FormData writes (join records, `_EntityData` projection, dependent-field
	 * fills) inherit the exact land-before-transaction-complete guarantee the `__Record` fix proved (the
	 * select-path caller `return`s this so the fence awaits an async cascade before its PostSolvers run).
	 * Also fired on the clear path (synchronously) so a subclass can clear dependent state.
	 *
	 * The base broker is a NO-OP — a plain entity Picker has no cascade, so existing migrated fields are
	 * unaffected. `MaterialsCascadePicker` and the bespoke `Materials*Picker` subclasses override this to
	 * reproduce the legacy MES `_onRecordRefresh` behavior. Return a Promise to make the transaction wait.
	 *
	 * @param {Object} pView - the dynamic-form view.
	 * @param {Record<string, any>} pInput - the field descriptor.
	 * @param {Record<string, any>|Array<Record<string, any>>} pRecord - what was just written to `__Record`
	 *   (an object in single mode, an array in multi mode, `{}`/`[]` on clear).
	 * @param {number|string} [pRowIndex] - the row index for a tabular picker.
	 * @param {string} [pTransactionGUID] - the active transaction (for a subclass that needs to fence its own async work).
	 * @return {void|Promise<any>}
	 */
	// eslint-disable-next-line no-unused-vars
	_onEntityRecordCascade(pView, pInput, pRecord, pRowIndex, pTransactionGUID)
	{
		// No-op default. The beast (§8.8) overrides this.
	}

	/**
	 * Choose what a single-record refresh writes. The base takes the server row; `MaterialsCascadePicker`
	 * reconciles it against the record the form already holds.
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {any} pHeldRecord - the record at the address when the row arrived.
	 * @param {Record<string, any>} pFetchedRecord - the server row.
	 * @param {string} pIdentityField @param {any} pValue @param {number|string|null} [pRowIndex]
	 * @return {Record<string, any>} the record to write.
	 */
	// eslint-disable-next-line no-unused-vars
	_reconcileRefreshedRecord(pView, pInput, pHeldRecord, pFetchedRecord, pIdentityField, pValue, pRowIndex)
	{
		return pFetchedRecord;
	}

	/**
	 * The per-InputType SELECTION-CLEARED seam, fired before any pathway blanks this field's value and
	 * `__Record` — the user clear, the anchor-change clear and the autofill-scope vet all route here.
	 *
	 * The base broker is a NO-OP: a plain entity Picker creates nothing, so it has nothing to tombstone
	 * and no salt to rotate. `MaterialsCascadePicker` overrides it to stage the scoped-EntityData
	 * tombstone and mint a fresh `__Salt`.
	 *
	 * @param {Object} pView - the dynamic-form view.
	 * @param {Record<string, any>} pInput - the field descriptor.
	 * @param {number|string|null} [pRowIndex] - the row index for a tabular picker.
	 */
	// eslint-disable-next-line no-unused-vars
	_handleEntitySelectionCleared(pView, pInput, pRowIndex)
	{
		// No-op default. MaterialsCascadePicker overrides this.
	}

	// --- Programmatic value commit + declarative defaults (beast B1, plan §8.8) ---

	/**
	 * Commit a PROGRAMMATIC value (a resolved Default, a cascade's reverse-seed, a creation flow) the way
	 * a user selection would land it, without double-driving the form cycle:
	 *   1. `setDataByInput` / `setDataTabularByHash` — the framework's programmatic set: writes the model
	 *      and runs this input's provider loop inside a fenced transaction, so the broker's own
	 *      `onDataChange` populates `__Record` and fires the cascade seam with the same
	 *      land-before-PostSolvers guarantee a user pick gets. (It does NOT solve/marshal — same as the
	 *      legacy MES `_setResolvedDefault`, whose select2 repainted via its own change plumbing.)
	 *   2. Sync the hidden informary input, so a later whole-form marshal-from-view cannot clobber the
	 *      model with the stale pre-commit DOM value.
	 *   3. `setValue` on the mounted picker widget — DISPLAY only (PSP's setValue deliberately fires no
	 *      OnChange). Routing through PSP's OnChange/_commit instead would call dataChanged a second
	 *      time: a double transaction + a double record refresh.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue
	 * @param {number|string|null} [pRowIndex]
	 */
	_setValueAndCommit(pView, pInput, pValue, pRowIndex)
	{
		if (pRowIndex != null)
		{
			if (typeof pView.setDataTabularByHash === 'function')
			{
				pView.setDataTabularByHash(pInput.PictForm.GroupIndex, pInput.Hash, pRowIndex, pValue);
			}
		}
		else if (typeof pView.setDataByInput === 'function')
		{
			pView.setDataByInput(pInput, pValue);
		}
		this._syncCommittedValueDOM(pInput, pValue, pRowIndex);
	}

	/**
	 * Steps 2–3 of _setValueAndCommit on their own: sync the hidden informary input + repaint the
	 * mounted widget (display-only) for a value the MODEL already holds. Used by the cascade family
	 * when it rewrites the committed value MID provider-loop (e.g. `__NEW__` → the synthesized GUID),
	 * where re-driving setDataByInput would nest a second provider pass.
	 * @param {Record<string, any>} pInput @param {any} pValue @param {number|string|null} [pRowIndex]
	 */
	_syncCommittedValueDOM(pInput, pValue, pRowIndex)
	{
		// The picker id helpers live on the (untyped-to-tsc) pict-section-picker base; reach them via a cast.
		const tmpSelf = /** @type {any} */ (this);
		const tmpCSV = Array.isArray(pValue) ? pValue.join(',') : ((pValue === undefined || pValue === null) ? '' : pValue);
		const tmpHiddenSelector = (pRowIndex != null) ? tmpSelf.getTabularHiddenID(pInput.Macro.RawHTMLID, pRowIndex) : pInput.Macro.HTMLSelector;
		const tmpPickerHash = (pRowIndex != null) ? tmpSelf.getTabularPickerHash(pInput.Macro.RawHTMLID, pRowIndex) : tmpSelf.getPickerHash(pInput.Macro.RawHTMLID);
		if (this.pict.ContentAssignment)
		{
			this.pict.ContentAssignment.assignContent(tmpHiddenSelector, tmpCSV);
		}
		const tmpPickerView = this.pict.views && this.pict.views[tmpPickerHash];
		if (tmpPickerView && typeof tmpPickerView.setValue === 'function')
		{
			tmpPickerView.setValue(pValue);
		}
	}

	/**
	 * The field the picker's committed value identifies a record by — PSP's ValueField. Mirrors the MES
	 * `_getRecordIdentity` (minus the creation-GUID branch, which arrives with the creation primitives
	 * in B2 Sample).
	 * @param {Record<string, any>} pInput @return {string}
	 */
	_getRecordIdentity(pInput)
	{
		const tmpPictForm = (pInput && pInput.PictForm) || {};
		return tmpPictForm.RecordIdentity || tmpPictForm.IdentityField || `ID${this._resolveEntity(tmpPictForm) || ''}`;
	}

	/**
	 * True when this InputType gets the legacy MES defaults-on-empty behavior (see the map's
	 * `MaterialsFamily` flag). GATE, not vestige: enabling descriptor Defaults base-wide would make
	 * already-shipped generic Select→Picker fields carrying a `Default` suddenly self-populate on new
	 * forms — a data-result change. A bespoke cascade subclass may override to opt in directly.
	 *
	 * The `MaterialsHidden*` leg is membership, not a widening: those InputTypes are served by the same
	 * providers and carry a descriptor `Default` as their normal shape (the generated test-slot
	 * descriptors are `Default: '<test hash>'`), so the map's omission of them dropped a behaviour the
	 * legacy applied. `_clearsOnAnchorChange` already reads the same membership.
	 * @param {Record<string, any>} pInput @return {boolean}
	 */
	_appliesConfiguredDefaults(pInput)
	{
		const tmpInputType = pInput?.PictForm?.InputType || '';
		if (tmpInputType.startsWith('MaterialsHidden'))
		{
			return true;
		}
		const tmpSpecialized = _SPECIALIZED_SELECTOR_MAP[tmpInputType];
		return !!(tmpSpecialized && tmpSpecialized.MaterialsFamily);
	}

	/**
	 * True when a declarative `Default` should be applied to the picker. Ported from the MES: a new form's
	 * marshal destination holds a DEFINED '' at the address, so manyfest's getValueByHash does NOT
	 * substitute the descriptor Default (it only does so for `undefined`) — pValue arrives '' and we apply
	 * it ourselves. Also fires when pValue DID arrive as the raw Default token (from an undefined address)
	 * for a by-field default, so the token can be resolved to an id; a by-id value already IS the record id
	 * and must not re-apply.
	 * @param {Record<string, any>} pInput @param {any} pValue @return {boolean}
	 */
	_shouldApplyConfiguredDefault(pInput, pValue)
	{
		if (!this._appliesConfiguredDefaults(pInput))
		{
			return false;
		}
		if (pInput.Default == null || pInput.Default === '')
		{
			return false;
		}
		if (pValue == null || pValue === '')
		{
			return true;
		}
		const tmpField = pInput.PictForm ? pInput.PictForm.DefaultField : null;
		return (pValue === pInput.Default && !!tmpField && tmpField !== this._getRecordIdentity(pInput));
	}

	/**
	 * Apply `pInput.Default` to an empty picker (ported from the MES). `PictForm.DefaultField` names a
	 * NON-identity field to match on (e.g. `Hash`) so the default is portable across customers —
	 * `Default:"ACPT"` + `DefaultField:"Hash"` resolves the customer's ACPT SampleType by its id via one
	 * read. Without a DefaultField (or when it IS the identity) the Default is already the id and is set
	 * directly. The commit closes the value-gate so this does not re-fire; a re-entrancy latch dedupes
	 * concurrent init passes while the read is in flight.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 */
	_applyConfiguredDefault(pView, pInput, pRowIndex)
	{
		if (pInput._DefaultResolving)
		{
			return;
		}
		const tmpField = pInput.PictForm ? pInput.PictForm.DefaultField : null;
		if (!tmpField || tmpField === this._getRecordIdentity(pInput))
		{
			this._setResolvedDefault(pView, pInput, pRowIndex, pInput.Default);
			return;
		}
		const tmpEntity = this._resolveEntity((pInput && pInput.PictForm) || {});
		if (!tmpEntity || !this.pict.HeadlightRestClient)
		{
			return;
		}
		pInput._DefaultResolving = true;
		this.pict.HeadlightRestClient.getJSON(`${tmpEntity}s/FilteredTo/FBV~${tmpField}~EQ~${encodeURIComponent(pInput.Default)}/0/1`, (pError, pRows) =>
		{
			pInput._DefaultResolving = false;
			const tmpRecord = (!pError && Array.isArray(pRows)) ? pRows[0] : null;
			if (tmpRecord)
			{
				this._setResolvedDefault(pView, pInput, pRowIndex, tmpRecord[this._getRecordIdentity(pInput)]);
			}
		});
	}

	/**
	 * Land a resolved default through the value-commit primitive (record refresh + cascade + repaint).
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex @param {any} pValue
	 */
	_setResolvedDefault(pView, pInput, pRowIndex, pValue)
	{
		if (pValue === undefined || pValue === null || pValue === '')
		{
			return;
		}
		this._setValueAndCommit(pView, pInput, pValue, pRowIndex);
	}

	/** The document's own project id (`ReportData.Project.IDProject`, else `ReportData.IDProject`), or null. @return {number|string|null} */
	_getDocumentProjectID()
	{
		const tmpReportData = this.pict.AppData?.DocumentData?.ReportData;
		const tmpIDProject = tmpReportData?.Project?.IDProject || tmpReportData?.IDProject;
		return (Number(tmpIDProject) > 0) ? tmpIDProject : null;
	}

	/**
	 * Legacy ProjectSelector parity: an empty project field on an editable document takes the document's project,
	 * which saves the field and publishes `AppData.SelectedProject` for sample naming. A descriptor Default wins.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {any} pValue @param {number|string|null} pRowIndex
	 * @return {string|null} - The committed project id, or null when nothing was committed.
	 */
	_applyDocumentProjectDefault(pView, pInput, pValue, pRowIndex)
	{
		if (!pInput || !pInput.PictForm || pInput.PictForm.InputType !== 'ProjectSelector')
		{
			return null;
		}
		if ((pInput.Default != null && pInput.Default !== '') || (pValue != null && pValue !== ''))
		{
			return null;
		}
		const tmpIDProject = this._getDocumentProjectID();
		if (!tmpIDProject || this.pict.AppData.DocumentData.ReportData.Locked)
		{
			return null;
		}
		this._setResolvedDefault(pView, pInput, pRowIndex, String(tmpIDProject));
		return String(tmpIDProject);
	}

	/**
	 * Wire the Bridge Search Panel to this picker (legacy BridgeSelector parity): a panel pick lands through the
	 * value-commit primitive, so the record refresh, cascade and repaint take the standard path. One callback
	 * slot, as legacy: the last BridgeSelector initialized wins.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} pRowIndex
	 */
	_wireBridgeSearchPanel(pView, pInput, pRowIndex)
	{
		const tmpPanel = this.pict.views.HeadlightPaneBridges;
		if (!tmpPanel || typeof tmpPanel.setBridgeSelectionCallback !== 'function')
		{
			return;
		}
		tmpPanel.setBridgeSelectionCallback((pBridge) =>
		{
			if (pBridge && pBridge.IDBridge) { this._setValueAndCommit(pView, pInput, pBridge.IDBridge, pRowIndex); }
		});
	}

	/** Apply a declarative Default (MES-family fields) before the mount, mirroring MES.onInputInitialize:
	 *  pValue is blanked so the mount never renders a raw unresolved token; the async resolve lands
	 *  through _setValueAndCommit. BridgeSelector also claims the Bridge Search Panel here. */
	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._forgetCustomValues(pInput, null);
		const tmpProjectDefault = this._applyDocumentProjectDefault(pView, pInput, pValue, null);
		if (tmpProjectDefault !== null)
		{
			pValue = tmpProjectDefault;
		}
		if (this._shouldApplyConfiguredDefault(pInput, pValue))
		{
			this._applyConfiguredDefault(pView, pInput, null);
			pValue = '';
		}
		if (pInput?.PictForm?.InputType === 'BridgeSelector')
		{
			this._wireBridgeSearchPanel(pView, pInput, null);
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/** Tabular twin of onInputInitialize's default application and Bridge Search Panel wiring. */
	onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._forgetCustomValues(pInput, pRowIndex);
		const tmpProjectDefault = this._applyDocumentProjectDefault(pView, pInput, pValue, pRowIndex);
		if (tmpProjectDefault !== null)
		{
			pValue = tmpProjectDefault;
		}
		if (this._shouldApplyConfiguredDefault(pInput, pValue))
		{
			this._applyConfiguredDefault(pView, pInput, pRowIndex);
			pValue = '';
		}
		if (pInput?.PictForm?.InputType === 'BridgeSelector')
		{
			this._wireBridgeSearchPanel(pView, pInput, pRowIndex);
		}
		return super.onInputInitializeTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	// --- Disabled-state parity (BUG-2) + open-dropdown re-render guard (BUG-1 interim) ---

	/** Register the Headlight Picker override CSS once (idempotent), above pict-section-picker's base block. */
	_ensureHeadlightPickerCSS()
	{
		if (this._headlightPickerCSSInjected) { return; }
		const tmpCSSMap = this.pict && this.pict.CSSMap;
		if (tmpCSSMap && typeof tmpCSSMap.addCSS === 'function')
		{
			tmpCSSMap.addCSS('Headlight-Picker-Overrides-CSS', _HEADLIGHT_PICKER_OVERRIDES_CSS, 600);
			if (typeof tmpCSSMap.injectCSS === 'function') { tmpCSSMap.injectCSS(); }
			this._headlightPickerCSSInjected = true;
		}
	}

	/**
	 * Reflect the descriptor's `PictForm.Disabled` onto the picker host as a `data-disabled` marker (BUG-2).
	 * pict-section-picker has no Disabled concept — its form-input maps only ReadOnly, which additionally
	 * strips the border + chevron — so a port-forwarded Disabled field would otherwise render as a fully
	 * interactive picker. The marker drives the greyed, non-interactive override CSS (chevron hidden),
	 * matching the sibling disabled inputs. Re-applied on every (re)mount because a full picker render
	 * rebuilds the widget DOM inside the host.
	 * @param {Record<string, any>} pInput @param {string} pHostSelector
	 */
	_reflectDisabledState(pInput, pHostSelector)
	{
		const tmpDisabled = !!(pInput && pInput.PictForm && pInput.PictForm.Disabled);
		const tmpElement = this.pict.ContentAssignment && this.pict.ContentAssignment.getElement(pHostSelector);
		const tmpHost = tmpElement && tmpElement[0];
		if (!tmpHost || typeof tmpHost.setAttribute !== 'function') { return; }
		if (tmpDisabled) { tmpHost.setAttribute('data-disabled', 'true'); }
		else if (typeof tmpHost.removeAttribute === 'function') { tmpHost.removeAttribute('data-disabled'); }
	}

	/** @param {string} pSelector @return {any} the first element the selector matches, or null. */
	_firstElement(pSelector)
	{
		const tmpElements = this.pict.ContentAssignment && this.pict.ContentAssignment.getElement(pSelector);
		return (tmpElements && tmpElements[0]) || null;
	}

	/**
	 * Reflect the descriptor's `PictForm.Required` onto the parts of the field the template can't reach
	 * (the template already marks the value input `data-required`): the value input's `data-empty-values`
	 * — this picker's unset sentinels, since an unset Number picker marshals in as 0, which InputValidation's
	 * value check would otherwise count as a selection — and `aria-required` on the widget's combobox.
	 * Re-applied after every (re)mount and commit because a picker render rebuilds the combobox.
	 * @param {Record<string, any>} pInput @param {string} pValueInputSelector @param {string} pHostSelector
	 */
	_reflectRequiredState(pInput, pValueInputSelector, pHostSelector)
	{
		const tmpRequired = !!(pInput && pInput.PictForm && pInput.PictForm.Required);
		const tmpValueInput = this._firstElement(pValueInputSelector);
		if (tmpValueInput && typeof tmpValueInput.setAttribute === 'function')
		{
			if (tmpRequired)
			{
				tmpValueInput.setAttribute('data-empty-values', JSON.stringify(this._resolveEmptyValues(pInput).map(String)));
			}
			else
			{
				tmpValueInput.removeAttribute('data-empty-values');
			}
		}
		const tmpHost = this._firstElement(pHostSelector);
		const tmpCombobox = (tmpHost && typeof tmpHost.querySelector === 'function') ? tmpHost.querySelector('[role="combobox"]') : null;
		if (!tmpCombobox)
		{
			return;
		}
		if (tmpRequired)
		{
			tmpCombobox.setAttribute('aria-required', 'true');
		}
		else
		{
			tmpCombobox.removeAttribute('aria-required');
		}
	}

	/**
	 * After a commit: re-apply the Required state the commit's render wiped, and — once the form has been
	 * submitted (`show-validation`) — revalidate the field, because a picker commit writes the value input
	 * without firing the DOM event InputValidation's form-level listener waits for.
	 * @param {Record<string, any>} pInput @param {string} pValueInputSelector @param {string} pHostSelector
	 */
	_syncRequiredStateAfterCommit(pInput, pValueInputSelector, pHostSelector)
	{
		this._reflectRequiredState(pInput, pValueInputSelector, pHostSelector);
		const tmpValidation = this.pict.providers && this.pict.providers.InputValidation;
		if (!tmpValidation || typeof tmpValidation.revalidateInputElement !== 'function')
		{
			return;
		}
		const tmpValueInput = this._firstElement(pValueInputSelector);
		if (!tmpValueInput || tmpValueInput.getAttribute('data-required') !== 'true')
		{
			return;
		}
		if (typeof tmpValueInput.closest !== 'function' || !tmpValueInput.closest('.show-validation'))
		{
			return;
		}
		tmpValidation.revalidateInputElement(tmpValueInput);
	}

	/**
	 * True when a picker is already mounted, its dropdown is OPEN, and its widget DOM is still present —
	 * i.e. the user is mid-interaction and this is a marshal re-render, not a section teardown.
	 *
	 * On a marshal-driven re-render (RenderMethod 'replace') we skip the destructive re-mount while the
	 * dropdown is open: the committed value is already reflected in the open picker, and skipping preserves
	 * the search box's text + focus mid-multi-pick (a full re-render rebuilds the search input empty). The
	 * DOM-presence check still lets a genuine teardown re-mount. (This originally also worked around a
	 * pre-1.9.0 position:fixed dropdown that detached on re-render; PSP 1.9.0's absolute/portal anchoring
	 * removed that failure mode, so the search/focus preservation is now the sole reason to keep the skip.)
	 * @param {string} pPickerHash @return {boolean}
	 */
	_isOpenAndMounted(pPickerHash)
	{
		const tmpView = this.pict.views && this.pict.views[pPickerHash];
		if (!tmpView || !tmpView._open) { return false; }
		if (typeof document === 'undefined') { return false; }
		return !!document.getElementById(`PPS_${pPickerHash}`);
	}

	/**
	 * Paint the Show-All toggle icon from the persisted override state on (re)mount — a full picker render
	 * rebuilds the field DOM, so the icon (default `fa-toggle-off` in the template) must be re-synced. No-op
	 * unless the field opts in via PictForm.ShowAllToggle or its config-map entry. Mirrors MES.onInputInitialize's icon sync.
	 * @param {Record<string, any>} pInput @param {number|string} [pRowIndex]
	 */
	_syncShowAllIcon(pInput, pRowIndex)
	{
		if (!libFilterToggleUI.isToggleVisible(pInput)) { return; }
		const tmpEngine = this.pict.providers.EntityFilterEngine;
		const tmpState = !!(tmpEngine && tmpEngine.isFilterOverridden(this._withMapFilterDefaults(pInput), (pRowIndex == null) ? undefined : pRowIndex));
		this._updateFilterToggleIcon(pInput.Macro.RawHTMLID, tmpState, pRowIndex);
		// The render-time macro already painted a manifest-declared Disabled; this catches a descriptor
		// disabled AFTER render, and re-paints it over the rebuilt field DOM a full picker render leaves.
		libFilterToggleUI.syncToggleDisabledState(this.pict, this.getSelectFilterToggleID(pInput.Macro.RawHTMLID, pRowIndex), libFilterToggleUI.isInputDisabled(pInput));
	}

	// --- Dynamic-filter dependent clearing (the legacy MES onAfterEventCompletion port, §9 parity) ---
	// When an ANCHOR field a picker depends on (PictForm.DynamicFilters[].TriggerGroupHash) changes, the
	// value already selected here may no longer belong to the new anchor. The engine narrows the OPTION
	// list per query, but only this pathway clears an already-committed stale value. Ported verbatim from
	// MaterialsEntitySelector (deleted in the §9 wave) and gated to the MES family like the legacy was —
	// generic entity pickers with TriggerGroupHash never auto-cleared legacy-side, so enabling it
	// base-wide would be a data-result change (the same reasoning as the _appliesConfiguredDefaults gate).

	/** True when this InputType inherits the legacy MES anchor-change clearing. @param {Record<string, any>} pInput */
	_clearsOnAnchorChange(pInput)
	{
		const tmpInputType = pInput?.PictForm?.InputType || '';
		return this._appliesConfiguredDefaults(pInput)
			|| tmpInputType === 'MaterialsEntitySelector'
			|| tmpInputType.startsWith('MaterialsHidden');
	}

	/**
	 * The shared clear decision: a TriggerGroup event from one of this input's DynamicFilters anchors
	 * clears the dependent value — unless the field opted into `PictForm.SmartClear`, which keeps the
	 * value when illegality can't be PROVEN (see _isSelectionIllegalForFilter).
	 * @param {Object} pView @param {Record<string, any>} pInput @param {string} pEvent
	 * @param {number|string|null} pRowIndex
	 */
	_maybeClearForDynamicFilter(pView, pInput, pEvent, pRowIndex)
	{
		if (!this._clearsOnAnchorChange(pInput)) { return; }
		const tmpMatchedFilter = (pRowIndex == null)
			? this._matchedDynamicFilterForEvent(pInput, pEvent)
			: this._matchedDynamicFilterForEventTabular(pInput, pEvent, pRowIndex);
		if (tmpMatchedFilter && (!pInput.PictForm?.SmartClear || this._isSelectionIllegalForFilter(pView, pInput, tmpMatchedFilter, pRowIndex)))
		{
			this._clearForDynamicFilter(pView, pInput, (pRowIndex == null) ? null : pRowIndex);
		}
	}

	/**
	 * Pure: does a global event target one of this input's DynamicFilters?
	 * Returns the matching filter entry, or null. Self-emitted events are ignored.
	 * @param {Record<string, any>} pInput @param {string} pEvent
	 * @return {object|null}
	 */
	_matchedDynamicFilterForEvent(pInput, pEvent)
	{
		const tmpDynamicFilters = pInput && pInput.PictForm ? pInput.PictForm.DynamicFilters : null;
		if (!Array.isArray(tmpDynamicFilters)) { return null; }
		const tmpParts = (typeof pEvent === 'string' ? pEvent : '').split(':');
		if (tmpParts[0] !== 'TriggerGroup') { return null; }
		const tmpGroupHash = tmpParts[1];
		const tmpEmitterHash = tmpParts[3];
		if ((pInput.Hash || pInput.DataAddress) === tmpEmitterHash) { return null; }
		return tmpDynamicFilters.find((pFilter) => pFilter && pFilter.TriggerGroupHash === tmpGroupHash) || null;
	}

	/**
	 * Tabular sibling of `_matchedDynamicFilterForEvent`. A tabular anchor fires a ROW-SCOPED event
	 * (`TriggerGroup:<hash>:RecordRefresh:<emitter>:<rowIndex>:<uuid>`); the global broadcast delivers it
	 * to EVERY row, so a dependent column must act on its OWN row only — otherwise changing one row's
	 * anchor would wipe valid selections in every other row. A global (non-row-scoped) event has a UUID
	 * in slot [4], which parseInt yields NaN for, so it never matches here.
	 * @param {Record<string, any>} pInput @param {string} pEvent @param {number|string} pRowIndex
	 * @return {object|null}
	 */
	_matchedDynamicFilterForEventTabular(pInput, pEvent, pRowIndex)
	{
		const tmpDynamicFilters = pInput && pInput.PictForm ? pInput.PictForm.DynamicFilters : null;
		if (!Array.isArray(tmpDynamicFilters)) { return null; }
		const tmpParts = (typeof pEvent === 'string' ? pEvent : '').split(':');
		if (tmpParts[0] !== 'TriggerGroup') { return null; }
		const tmpGroupHash = tmpParts[1];
		const tmpEmitterHash = tmpParts[3];
		const tmpEventRowIndex = parseInt(tmpParts[4], 10);
		if ((pInput.Hash || pInput.DataAddress) === tmpEmitterHash) { return null; }
		if (isNaN(tmpEventRowIndex) || tmpEventRowIndex !== Number(pRowIndex)) { return null; }
		return tmpDynamicFilters.find((pFilter) => pFilter && pFilter.TriggerGroupHash === tmpGroupHash) || null;
	}

	/**
	 * Smart-clear gate (opt-in via `PictForm.SmartClear`). Returns true only when this picker's resolved
	 * record is DEFINITIVELY illegal for the changed anchor — the record carries the filter's `Field` and
	 * its value no longer equals the anchor's current value. When the record isn't resolved yet, lacks the
	 * FK, or the anchor is unset, illegality can't be proven, so we keep the value: this avoids clobbering a
	 * just-autofilled selection during sample projection, and preserves a user override that sits outside
	 * the filter.
	 * @param {Object} pView @param {Record<string, any>} pInput
	 * @param {object} pFilter - the matched DynamicFilters entry (`{ Field, FromField | FromRowField, … }`).
	 * @param {number|string|null} [pRowIndex]
	 * @return {boolean} true only when the current selection is provably illegal for pFilter.
	 */
	_isSelectionIllegalForFilter(pView, pInput, pFilter, pRowIndex)
	{
		if (!pFilter || !pFilter.Field) { return false; }
		const tmpRecord = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex));
		if (!tmpRecord || typeof tmpRecord !== 'object' || Array.isArray(tmpRecord) || !(pFilter.Field in tmpRecord))
		{
			return false;
		}
		let tmpAnchorValue;
		if (pFilter.FromRowField && pRowIndex != null && pInput.PictForm && pInput.PictForm.InformaryContainerAddress)
		{
			const tmpRowAddress = this.pict.providers.Informary.getComposedContainerAddress(pInput.PictForm.InformaryContainerAddress, pRowIndex, pFilter.FromRowField);
			tmpAnchorValue = this.pict.manifest.getValueAtAddress(pView.getMarshalDestinationObject(), tmpRowAddress);
		}
		else if (pFilter.FromField)
		{
			tmpAnchorValue = this.pict.manifest.getValueByHash(this.pict, pFilter.FromField);
		}
		else
		{
			return false;
		}
		// An unset anchor imposes no constraint (applyDynamicFilters omits it), so nothing is illegal.
		const tmpAnchorNormalized = (tmpAnchorValue === null || tmpAnchorValue === undefined) ? '' : String(tmpAnchorValue).trim();
		if (tmpAnchorNormalized === '' || tmpAnchorNormalized === '0') { return false; }
		return String(tmpRecord[pFilter.Field]) !== tmpAnchorNormalized;
	}

	/**
	 * Reset this picker's value and its resolved-record companion (`__Record`), committed through
	 * _setValueAndCommit so the model, the hidden informary input and the mounted widget all reflect the
	 * cleared state (the legacy select2 val(null) + re-marshal, in Picker terms). `__Record` is reset to
	 * an EMPTY OBJECT (not deleted) to match the selector convention — record flows write fields into it
	 * and assume it is always an object. The next dropdown open re-fetches with the updated DynamicFilters
	 * (the new anchor value).
	 *
	 * Routed through `_handleEntitySelectionCleared` FIRST, so a programmatic clear of a creation-enabled
	 * field mints its tombstone and re-salts exactly like a user clear does — reusing the `__Salt` would
	 * make a later re-add upsert into the soft-deleted row.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 */
	_clearForDynamicFilter(pView, pInput, pRowIndex)
	{
		try
		{
			this._handleEntitySelectionCleared(pView, pInput, pRowIndex);
			this.pict.manifest.setValueAtAddress(pView.getMarshalDestinationObject(), this._getRecordAddress(pInput, pRowIndex), {});
			this._setValueAndCommit(pView, pInput, pInput.Default ?? '', pRowIndex);
			this.pict.log.info(`DynamicFilter: cleared dependent input [${pInput.Hash}]${pRowIndex != null ? ` row ${pRowIndex}` : ''} after its anchor changed.`);
		}
		catch (pError)
		{
			this.pict.log.error(`DynamicFilter: failed to clear dependent input [${pInput && pInput.Hash}]: ${pError}`);
		}
	}

	/**
	 * Pure: does a global event correspond to an AUTOFILL into this input (an AutofillTriggerGroup entry
	 * whose TriggerGroupHash matches the event)? Self-emitted events, and entries flagged
	 * `AllowInvalidProjection` (record-projection sources whose out-of-scope values are legitimate), are
	 * skipped.
	 * @param {Record<string, any>} pInput @param {string} pEvent
	 * @return {object|null}
	 */
	_matchedAutofillGroupForEvent(pInput, pEvent)
	{
		let tmpGroups = (pInput && pInput.PictForm) ? pInput.PictForm.AutofillTriggerGroup : null;
		if (!tmpGroups) { return null; }
		if (!Array.isArray(tmpGroups)) { tmpGroups = [ tmpGroups ]; }
		const tmpParts = (typeof pEvent === 'string' ? pEvent : '').split(':');
		if (tmpParts[0] !== 'TriggerGroup') { return null; }
		const tmpGroupHash = tmpParts[1];
		const tmpEmitterHash = tmpParts[3];
		if ((pInput.Hash || pInput.DataAddress) === tmpEmitterHash) { return null; }
		return tmpGroups.find((pGroup) => pGroup && pGroup.TriggerGroupHash === tmpGroupHash && !pGroup.AllowInvalidProjection) || null;
	}

	/**
	 * Vet this picker's current value against its own Through DynamicFilters and suppress it when it is
	 * PROVABLY not a legal option — the selected `ThroughSelect` id is absent from the resolved allowed
	 * set for the current anchor. Fail-open everywhere else (no anchor, unprimed scope, no value → keep).
	 * Async because query-strategy Through slices load on demand — which also means it runs after the
	 * synchronous autofill has landed the value. Opt-in via PictForm.VetAutofillScope.
	 * @param {Object} pView @param {Record<string, any>} pInput @param {number|string|null} [pRowIndex]
	 * @return {Promise<void>}
	 */
	async _vetAutofilledValueAgainstFilters(pView, pInput, pRowIndex)
	{
		const tmpFilters = (pInput && pInput.PictForm) ? pInput.PictForm.DynamicFilters : null;
		const tmpResolver = this.pict.providers.ThroughScopeResolver;
		if (!Array.isArray(tmpFilters) || !tmpResolver) { return; }
		const tmpThroughFilters = tmpFilters.filter((pFilter) => pFilter && pFilter.Through && pFilter.ThroughSelect && pFilter.FromField);
		if (!tmpThroughFilters.length) { return; }
		const tmpDestination = pView.getMarshalDestinationObject();
		for (const tmpFilter of tmpThroughFilters)
		{
			const tmpAnchor = this.pict.manifest.getValueByHash(this.pict, tmpFilter.FromField);
			const tmpAnchorNormalized = (tmpAnchor === null || tmpAnchor === undefined) ? '' : String(tmpAnchor).trim();
			if (tmpAnchorNormalized === '' || tmpAnchorNormalized === '0') { continue; }
			const tmpRecord = this.pict.manifest.getValueAtAddress(tmpDestination, this._getRecordAddress(pInput, pRowIndex));
			let tmpSelected = (tmpRecord && typeof tmpRecord === 'object' && !Array.isArray(tmpRecord)) ? tmpRecord[tmpFilter.ThroughSelect] : null;
			if ((tmpSelected === null || tmpSelected === undefined || tmpSelected === '') && pRowIndex == null)
			{
				tmpSelected = this.pict.manifest.getValueAtAddress(tmpDestination, pInput.DataAddress);
			}
			if (tmpSelected === null || tmpSelected === undefined || tmpSelected === '' || Number(tmpSelected) === 0) { continue; }
			await tmpResolver.primeThroughSlice(tmpFilter, tmpAnchor);
			const tmpAllowed = tmpResolver.getThroughIDs(tmpFilter, tmpAnchor);
			// Fail-open backstop: an empty allowed set means the scope could not be resolved (sparse or missing
			// join data, or a failed prime) — that is "cannot constrain", not "everything is illegal". Clearing
			// on it would nuke a legitimately-chosen value. A genuinely-scoped anchor returns a non-empty set
			// and still clears an out-of-scope value below.
			if (!tmpAllowed || typeof tmpAllowed.has !== 'function' || tmpAllowed.size === 0)
			{
				continue;
			}
			if (!tmpAllowed.has(Number(tmpSelected)))
			{
				this._clearForDynamicFilter(pView, pInput, pRowIndex);
				return;
			}
		}
	}

	/**
	 * True while a record-refresh cascade is fanning out — a LOAD is authoritative, so persisted values
	 * must display rather than be re-vetted against the current filter scope. The counter lives on the
	 * pict instance under the name `MaterialsEntitySelector` sets, so a cascade fired by either family
	 * suppresses the vet in the other while both run on one form.
	 * @return {boolean}
	 */
	_isHydrating()
	{
		return !!this.pict._MaterialsEntityHydrationDepth;
	}

	/** The anchor-change event pathway (the legacy MES onAfterEventCompletion, ported): dependent clear +
	 *  autofill-scope vetting + idempotent scope re-priming for the new anchor. */
	onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID)
	{
		this._maybeClearForDynamicFilter(pView, pInput, pEvent, null);
		if (pInput.PictForm?.VetAutofillScope && !this._isHydrating() && this._matchedAutofillGroupForEvent(pInput, pEvent))
		{
			this._vetAutofilledValueAgainstFilters(pView, pInput, null).catch((pError) =>
			{
				this.pict.log.error(`Autofill scope vet failed (value kept): ${pError?.message || pError}`);
			});
		}
		// Warm any query-strategy Through slice / lab scope for the new anchor value so a dependent
		// picker's INN clause resolves synchronously once it is opened. Both are idempotent (keyed
		// re-fetch only on change).
		this.pict.providers.ThroughScopeResolver?.primeQueryScopesFromForm?.();
		this.pict.providers.LabScopeResolver?.primeFromForm?.();
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		return super.onAfterEventCompletion(pView, pInput, pValue, pHTMLSelector, pEvent, pTransactionGUID);
	}

	/** Tabular twin: act on this row's own anchor events only (see _matchedDynamicFilterForEventTabular). */
	onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID)
	{
		this._maybeClearForDynamicFilter(pView, pInput, pEvent, pRowIndex);
		if (pInput.PictForm?.VetAutofillScope && !this._isHydrating() && this._matchedAutofillGroupForEvent(pInput, pEvent))
		{
			this._vetAutofilledValueAgainstFilters(pView, pInput, pRowIndex).catch((pError) =>
			{
				this.pict.log.error(`Autofill scope vet failed (value kept): ${pError?.message || pError}`);
			});
		}
		this.pict.providers.ThroughScopeResolver?.primeQueryScopesFromForm?.();
		this.pict.providers.LabScopeResolver?.primeFromForm?.();
		// @ts-ignore — inherited through the PSP chain tsc cannot see.
		return super.onAfterEventTabularCompletion(pView, pInput, pValue, pHTMLSelector, pRowIndex, pEvent, pTransactionGUID);
	}

	/** Override the mount: guard the destructive open re-render (BUG-1) + stamp the disabled marker (BUG-2)
	 *  and the Required state + sync the Show-All toggle icon. */
	_mountPicker(pView, pInput, pValue, pHostSelector, pPickerHash, fOnChange)
	{
		this._ensureHeadlightPickerCSS();
		if (this._isOpenAndMounted(pPickerHash)) { return true; }
		// A null row keeps the BaseFilter row-less while giving _applyFacetRules the view.
		const tmpMounted = this._withTabularContext(pView, null, () => super._mountPicker(pView, pInput, pValue, pHostSelector, pPickerHash, fOnChange), pValue);
		if (tmpMounted)
		{
			this._reflectDisabledState(pInput, pHostSelector);
			this._reflectRequiredState(pInput, pInput.Macro.HTMLSelector, pHostSelector);
			this._syncShowAllIcon(pInput);
		}
		return tmpMounted;
	}

	/** Tabular twin of _mountPicker. */
	_mountPickerTabular(pView, pInput, pValue, pRowIndex)
	{
		this._ensureHeadlightPickerCSS();
		// The tabular id helpers live on the (untyped-to-tsc) pict-section-picker base; reach them via a cast.
		const tmpSelf = /** @type {any} */ (this);
		const tmpHash = tmpSelf.getTabularPickerHash(pInput.Macro.RawHTMLID, pRowIndex);
		if (this._isOpenAndMounted(tmpHash)) { return true; }
		// Stash the row context so the _buildPickerConfig inside the super mount captures it into this row's
		// BaseFilter closure (plan §5.3). The arrow preserves super binding.
		const tmpMounted = this._withTabularContext(pView, pRowIndex, () => super._mountPickerTabular(pView, pInput, pValue, pRowIndex), pValue);
		if (tmpMounted)
		{
			const tmpHostSelector = tmpSelf.getTabularPickerHostID(pInput.Macro.RawHTMLID, pRowIndex);
			this._reflectDisabledState(pInput, tmpHostSelector);
			this._reflectRequiredState(pInput, tmpSelf.getTabularHiddenID(pInput.Macro.RawHTMLID, pRowIndex), tmpHostSelector);
			this._syncShowAllIcon(pInput, pRowIndex);
		}
		return tmpMounted;
	}
}

module.exports = PictProviderInputTypePicker;

module.exports.default_configuration = _DEFAULT_CONFIGURATION;
